// Ultimate Card Game (UCG) - Client-Side State Streaming Engine
//
// Production online matches are server-authoritative (server/gameSimulation.js,
// 60 Hz fixed timestep) - see CLAUDE.md "Die eine Regel: Server-Autorität".
// The WORLD_STATE packets this module's applyWorldState() consumes are built
// server-side by GameSimulation.serializeStateForTeam()/serializeStateForBothTeams(),
// which mask the opponent's hand and undetected cloaked units before they ever
// leave the server (K-1.1). applyWorldState() below interpolates those
// incoming snapshots into the local battlefield.
//
// serializeWorldState() below is NOT part of that production path - nothing
// in main.js/wsMatchClient.js calls it. It survives purely as a test-fixture
// builder (tests/netcode_test.mjs, tests/performance_integrity_test.mjs) for
// exercising applyWorldState()'s round-trip/interpolation logic without a
// real server. Unlike the server's serializer, it does NOT mask the
// opponent's hand (see guestHand below) - safe today only because nothing
// wires its output onto an actual connection to an opponent. Do not call it
// from production code without adding the same K-1.1 masking first.

import { CARDS_DATA, getCardByName, getCardByModuleId } from '../data/cards.js';
import { hasCustomSpawnSound } from '../data/unitSounds.js';
import { expandSnapshotUnits } from './snapshotCompaction.js';
import {
  SENTINAL_LOCK_GLOB_COLOR,
  SENTINAL_LOCK_LOB_OPTIONS,
  SENTINAL_LOCK_MUZZLE_OFFSET_Y,
  buildSentinalGlobPalette
} from '../engine/unit.js';

// How many inter-arrival gaps getNetStats() below averages/jitters over -
// ~2s of history at the nominal 30 Hz broadcast rate (server/matchRoom.js),
// long enough to smooth out normal per-packet scheduling noise without
// hiding a real, sustained rate change for more than a couple of seconds.
const SNAPSHOT_HISTORY_SIZE = 60;

const sentinalLobOptionsCache = new Map();
function getSentinalLobOptions(customColor) {
  if (!customColor) return SENTINAL_LOCK_LOB_OPTIONS;
  let cached = sentinalLobOptionsCache.get(customColor);
  if (!cached) {
    cached = { ...SENTINAL_LOCK_LOB_OPTIONS, palette: buildSentinalGlobPalette(customColor) };
    sentinalLobOptionsCache.set(customColor, cached);
  }
  return cached;
}

// Max identical sounds played per WORLD_STATE packet (docs/PERFORMANCE_AND_ONLINE_STABILITY_PLAN_V2_2026-09.md
// §6.2). A packet with e.g. 15 Marines firing in the same tick would otherwise
// call audio.playGauss() 15 times in the same millisecond, causing WebAudio
// polyphonic voice exhaustion and audible crackling on iOS Safari/macOS. Keyed
// by "method:subtype" (e.g. "playExplosion:small" vs "playExplosion:large") so
// distinct sound variants don't suppress each other - purely a client-side
// cosmetic playback cap, it never touches simulation state or particle spawns.
const AUDIO_BURST_MAX_PER_TICK = 2;

// Wraps `audio` so every `playXxx(...)` call is counted per WORLD_STATE packet
// and silently dropped past AUDIO_BURST_MAX_PER_TICK; everything else on the
// object (duckBGM, setMatchTension, ...) passes through untouched. `burstMap`
// is cleared by the caller once per incoming packet, not by this wrapper, so a
// single Map/Proxy pair can be reused across the whole match without
// reallocating either per tick.
function wrapAudioBurstLimiter(audio, burstMap) {
  if (!audio) return audio;
  return new Proxy(audio, {
    get(target, prop, receiver) {
      const orig = Reflect.get(target, prop, receiver);
      if (typeof orig !== 'function' || typeof prop !== 'string' || !prop.startsWith('play')) {
        return orig;
      }
      return function throttledPlay(...args) {
        const subtype = typeof args[0] === 'string' ? args[0] : '';
        const key = subtype ? `${prop}:${subtype}` : prop;
        const count = (burstMap.get(key) || 0) + 1;
        burstMap.set(key, count);
        if (count <= AUDIO_BURST_MAX_PER_TICK) {
          return orig.apply(target, args);
        }
        return undefined;
      };
    }
  });
}

export class StateStreamManager {
  constructor(game) {
    this.game = game;
    this.seq = 0;
    this.lastReceivedSeq = -1;
    this.lastPacketTime = 0;
    // Rolling buffer of ms between consecutive WORLD_STATE arrivals - see
    // getNetStats() and applyWorldState() below. Feeds the in-game net
    // overlay's Snapshot/Jitter rows (js/engine/performance.js, K-3.3).
    this._snapshotIntervalsMs = [];
    this._initialCardSyncDone = false;
    this._initialUnitSyncDone = false;
    this._initialModuleSyncDone = false;
    this._knownPendingModuleIds = { western: new Set(), eastern: new Set() };
    this._remoteMapScratch = new Map();
    this._scratchStrikeIds = new Set();
    // Per-packet audio dedup counters for wrapAudioBurstLimiter() below -
    // one Map reused for the whole match, cleared at the top of every
    // packet.events loop in applyWorldState().
    this._audioBurstMap = new Map();
    this._gatedAudioCache = null;
    this._gatedAudioSource = null;
  }

  reset() {
    this.seq = 0;
    this.lastReceivedSeq = -1;
    this.lastPacketTime = 0;
    this._snapshotIntervalsMs = [];
    this._initialCardSyncDone = false;
    this._initialUnitSyncDone = false;
    this._initialModuleSyncDone = false;
    this._knownPendingModuleIds.western.clear();
    this._knownPendingModuleIds.eastern.clear();
    this._remoteMapScratch.clear();
    this._scratchStrikeIds.clear();
    this._audioBurstMap.clear();
    this._gatedAudioCache = null;
    this._gatedAudioSource = null;
  }

  // TEST-FIXTURE ONLY - see file header. Not called by production code and
  // does not mask the opponent's hand; do not wire this into the real
  // client/server pipeline.
  serializeWorldState(game) {
    const b = game.battlefield;
    const eco = game.economy;
    const cs = game.cardSystem;
    if (!b || !b.bases || !eco) return null;

    const liveUnits = [];
    for (let i = 0; i < b.units.length; i++) {
      const u = b.units[i];
      if (u.isDead) continue;
      liveUnits.push({
        id: u.id,
        name: u.cardData?.name || u.name,
        team: u.team,
        x: Math.round(u.x * 10) / 10,
        y: Math.round(u.y * 10) / 10,
        hp: Math.round(u.hp),
        maxHp: u.maxHp,
        isAir: !!u.isAir,
        isCloaked: !!u.isCloaked,
        decloakTimer: u.decloakTimer ? Math.round(u.decloakTimer * 10) / 10 : 0,
        isParachuting: !!u.isParachuting,
        parachuteTimer: u.parachuteTimer ? Math.round(u.parachuteTimer * 100) / 100 : 0,
        parachuteAltitude: u.parachuteAltitude ? Math.round(u.parachuteAltitude * 10) / 10 : 0,
        burstTimer: u.burstTimer ? Math.round(u.burstTimer * 100) / 100 : 0,
        isReloading: !!u.isReloading,
        rocketsFired: u.rocketsFired || 0,
        deployProgress: u.deployProgress !== undefined ? Math.round(u.deployProgress * 100) / 100 : 0,
        underFireTimer: u.underFireTimer ? Math.round(u.underFireTimer * 100) / 100 : 0,
        smokeCloakTimer: u.smokeCloakTimer ? Math.round(u.smokeCloakTimer * 10) / 10 : 0,
        smokeGrenadeTriggered: !!u.smokeGrenadeTriggered,
        executionTimer: u.executionTimer ? Math.round(u.executionTimer * 100) / 100 : 0,
        executionTargetId: u.executionTargetId || null,
        executionCooldown: u.executionCooldown ? Math.round(u.executionCooldown * 10) / 10 : 0,
        targetId: u.target ? (u.target.id || (u.target === b.bases.western ? 'base_western' : (u.target === b.bases.eastern ? 'base_eastern' : null))) : null,
        attackCooldown: Math.round((u.attackCooldown || 0) * 100) / 100,
        isAttacking: (u.isHyperPanzer && !u.isStoppedAtBase) ? false : ((u.attackCooldown || 0) > 0 && !!u.target),
        facingDir: u.facingDir || 0,
        speed: u.speed || 0,
        isPincerStalking: !!u.isPincerStalking,
        isHeld: !!u.isHeld,
        isEngaged: !!u.isEngaged,
        edition: u.edition || null,
        customAuraHex: u.customAuraHex || null,
        tetherTargetId: u.tetherTargetId || null,
        beamTargetId: u.beamTargetId || null,
        dualTetherTarget1Id: u.dualTetherTarget1Id || null,
        dualTetherTarget2Id: u.dualTetherTarget2Id || null,
        tether1LockTimer: u.tether1LockTimer ? Math.round(u.tether1LockTimer * 100) / 100 : 0,
        isRooted: !!u.isRooted,
        acidStacks: u.acidStacks || 0,
        sporeSlowTimer: u.sporeSlowTimer ? Math.round(u.sporeSlowTimer * 10) / 10 : 0,
        sporeSlowPenalty: u.sporeSlowPenalty || 0,
        disarmTimer: u.disarmTimer ? Math.round(u.disarmTimer * 10) / 10 : 0,
        incinerateStacks: (u.incinerateStacks && u.incinerateStacks.length > 0) ? u.incinerateStacks.map(s => ({
          duration: s.duration || s.remaining,
          remaining: Math.round(s.remaining * 10) / 10,
          tickTimer: Math.round((s.tickTimer !== undefined ? s.tickTimer : 0.5) * 100) / 100,
          dps: s.dps || 2,
          attackerTeam: s.attackerTeam || null
        })) : [],
        plasmaChargeTimer: u.plasmaChargeTimer ? Math.round(u.plasmaChargeTimer * 100) / 100 : 0,
        plasmaMorphed: !!u.plasmaMorphed,
        plasmaTargetIsAir: !!u.plasmaTargetIsAir,
        isGoldenStatue: !!u.isGoldenStatue,
        hasGoldPedestal: !!u.hasGoldPedestal,
        isFallingStatue: !!u.isFallingStatue,
        isCocoon: !!u.isCocoon,
        cocoonTimer: u.cocoonTimer ? Math.round(u.cocoonTimer * 10) / 10 : 0,
        isFallingCocoon: !!u.isFallingCocoon,
        hasPetrifiedUnit: !!u.hasPetrifiedUnit,
        hasParasite: !!u.hasParasite,
        isInsane: !!u.isInsane,
        isParasitized: !!u.isParasitized,
        overstimulationTimer: u.overstimulationTimer ? Math.round(u.overstimulationTimer * 100) / 100 : 0,
        isOverstimulated: !!u.isOverstimulated,
        isRetreating: !!u.isRetreating,
        isBastionRearGuard: !!u.isBastionRearGuard,
        isPriorityTarget: !!u.isPriorityTarget,
        isMineRising: !!u.isMineRising,
        mineRiseTimer: u.mineRiseTimer ? Math.round(u.mineRiseTimer * 100) / 100 : 0,
        isBurrowing: !!u.isBurrowing,
        burrowTimer: u.burrowTimer ? Math.round(u.burrowTimer * 100) / 100 : 0,
        isBurrowed: !!u.isBurrowed,
        mineState: u.mineState || null,
        wasBurrowedBeforeRising: !!u.wasBurrowedBeforeRising,
        burrowAnchorX: u.burrowAnchorX !== undefined && u.burrowAnchorX !== null ? Math.round(u.burrowAnchorX * 10) / 10 : null,
        burrowAnchorY: u.burrowAnchorY !== undefined && u.burrowAnchorY !== null ? Math.round(u.burrowAnchorY * 10) / 10 : null,
        isGrowing: !!u.isGrowing,
        growthTimer: u.growthTimer ? Math.round(u.growthTimer * 100) / 100 : 0,
        snapStrikeTimer: u.snapStrikeTimer ? Math.round(u.snapStrikeTimer * 100) / 100 : 0,
        snapTargetId: u.snapTargetId || null,
        firstHitDone: !!u.firstHitDone,
        hasDealtFirstHit: !!u.hasDealtFirstHit,
        pinkBeretState: u.pinkBeretState || null,
        pinkBeretAimTimer: u.pinkBeretAimTimer ? Math.round(u.pinkBeretAimTimer * 100) / 100 : 0,
        pinkBeretTransitionTimer: u.pinkBeretTransitionTimer ? Math.round(u.pinkBeretTransitionTimer * 100) / 100 : 0,
        pinkBeretTargetId: u.pinkBeretTargetId || null,
        hasFiredInitialRocket: !!u.hasFiredInitialRocket,
        isStoppedAtBase: !!u.isStoppedAtBase,
        hackerState: u.hackerState || null,
        hackerTargetId: u.hackerTargetId || null,
        hackerLockTimer: u.hackerLockTimer ? Math.round(u.hackerLockTimer * 100) / 100 : 0,
        isHacking: !!u.isHacking,
        isTranscendent: !!u.isTranscendent,
        transcendentTimer: u.transcendentTimer ? Math.round(u.transcendentTimer * 100) / 100 : 0,
        handRevealTimer: u.handRevealTimer ? Math.round(u.handRevealTimer * 100) / 100 : 0,
        isDisabled: !!u.isDisabled,
        disabledByHackerId: u.disabledByHackerId || null,
        isIndestructible: !!u.isIndestructible,
        _hpRegenFlash: u._hpRegenFlash ? Math.round(u._hpRegenFlash * 100) / 100 : 0,
        isSentinelIdle: !!u.isSentinelIdle,
        turretAimState: u.turretAimState || null,
        hasBomb: !!u.hasBomb,
        pigeonState: u.pigeonState || null,
        bombTargetId: u.bombTargetId || null,
        bombHoverTimer: u.bombHoverTimer ? Math.round(u.bombHoverTimer * 100) / 100 : 0,
        isFlyingBackward: !!u.isFlyingBackward,
        isHovering: !!u.isHovering,
        pigeonPitch: u.pigeonPitch ? Math.round(u.pigeonPitch * 100) / 100 : 0,
        nozzleTilt: u.nozzleTilt ? Math.round(u.nozzleTilt * 100) / 100 : 0,
        age: Math.round((u.age || 0) * 100) / 100
      });
    }

    // Unmasked on purpose for this test fixture - see the file header and
    // the warning on serializeWorldState() above. The production server
    // serializer (server/gameSimulation.js _personalizeSnapshot()) replaces
    // this with the K-1.1 '···' placeholder before it ever reaches a client.
    const guestHand = cs?.hands?.eastern ? cs.hands.eastern.map(c => {
      if (!c) return null;
      return { name: c.name, slot: c.slot, id: c.instanceId };
    }) : null;

    const hostHandCount = cs?.hands?.western ? cs.hands.western.filter(Boolean).length : 0;

    return {
      type: 'WORLD_STATE',
      seq: this.seq++,
      t: performance.now(),
      baseHp: {
        w: Math.round(b.bases.western.hp),
        e: Math.round(b.bases.eastern.hp)
      },
      // maxHp is no longer a constant - every landed base module raises it
      // (js/engine/modules.js), so the Guest needs both numbers or its HP
      // bar renders against the wrong denominator.
      baseMaxHp: {
        w: Math.round(b.bases.western.maxHp),
        e: Math.round(b.bases.eastern.maxHp)
      },
      modules: b.moduleSystem ? b.moduleSystem.serialize() : null,
      plasma: {
        w: Math.round(eco.resources?.western?.plasma || 0),
        e: Math.round(eco.resources?.eastern?.plasma || 0)
      },
      divider: Math.round(b.territoryDivider * 10) / 10,
      control: b.territoryControl,
      bonus: b.territoryBonus,
      isTerritoryLocked: !!b.isTerritoryLocked,
      territoryLockedTeam: b.territoryLockedTeam || null,
      guestHand,
      hostHandCount,
      concentrationSeq: cs ? (cs.concentrationSeq || 0) : 0,
      lastConcentrationSlot: cs ? (cs.lastConcentrationSlot || null) : null,
      mentalExtractionSeq: cs ? (cs.mentalExtractionSeq || 0) : 0,
      lastMentalExtractionSlot: cs ? (cs.lastMentalExtractionSlot || null) : null,
      meltSeq: cs ? (cs.meltSeq || 0) : 0,
      lastMeltSlot: cs ? (cs.lastMeltSlot || null) : null,
      units: liveUnits,
      events: b.consumeFrameEvents()
    };
  }

  // Applies the authoritative state snapshot on the Guest with smooth lerping
  applyWorldState(game, packet, lerpFactor = 0.45) {
    if (!packet || packet.type !== 'WORLD_STATE') return false;
    if (packet.seq !== undefined && packet.seq < this.lastReceivedSeq) {
      // Out-of-order packet arriving late over network - skip
      return false;
    }
    this.lastReceivedSeq = packet.seq || 0;
    const now = performance.now();
    if (this.lastPacketTime > 0) {
      this._snapshotIntervalsMs.push(now - this.lastPacketTime);
      if (this._snapshotIntervalsMs.length > SNAPSHOT_HISTORY_SIZE) this._snapshotIntervalsMs.shift();
    }
    this.lastPacketTime = now;

    const b = game.battlefield;
    const eco = game.economy;
    const cs = game.cardSystem;
    const particles = game.particles;
    const rawAudio = game.audio;

    if (!b || !b.bases || !eco) return false;

    // The server omits unit fields that sit at their idle default
    // (js/net/snapshotCompaction.js); restore them so everything below sees
    // the complete packet. Idempotent for already-complete packets.
    expandSnapshotUnits(packet.units);

    // 1. Authoritative base modules, then base HP. Modules first, and
    // applySerialized() deliberately does not touch base HP: baseMaxHp/baseHp
    // below are authoritative, so re-adding each module's HP here would
    // double-count it on every packet.
    if (packet.modules && b.moduleSystem) {
      const shouldPlayModuleSound = this._initialModuleSyncDone;
      for (const teamKey of ['western', 'eastern']) {
        const remotePending = packet.modules[teamKey]?.pending || [];
        const knownSet = this._knownPendingModuleIds[teamKey];
        const currentIds = new Set();
        for (let i = 0; i < remotePending.length; i++) {
          const modId = remotePending[i].id;
          if (!modId) continue;
          currentIds.add(modId);
          if (shouldPlayModuleSound && !knownSet.has(modId)) {
            const modCard = getCardByModuleId(modId);
            const race = modCard?.race || b.bases?.[teamKey]?.race || 'Human';
            const isFriendlyMod = (teamKey === game.myTeam || (game.is2v2 && ((game.myTeam === 'western' && teamKey === 'western') || (game.myTeam === 'eastern' && teamKey === 'eastern'))));
            if (isFriendlyMod) {
              rawAudio?.playBark?.(race, 'spawn');
            }
          }
        }
        knownSet.clear();
        for (const id of currentIds) knownSet.add(id);
      }
      this._initialModuleSyncDone = true;

      b.moduleSystem.applySerialized(
        packet.modules,
        (moduleId) => getCardByModuleId(moduleId) || null
      );
    }

    if (packet.baseMaxHp) {
      b.bases.western.maxHp = packet.baseMaxHp.w;
      b.bases.eastern.maxHp = packet.baseMaxHp.e;
    }

    if (packet.baseHp) {
      b.bases.western.hp = packet.baseHp.w;
      b.bases.eastern.hp = packet.baseHp.e;
      // Game-over is decided exclusively by the server's own authoritative
      // MATCH_END message (js/main.js wsClient.onMatchEnd), never guessed
      // locally from an interpolated HP snapshot: that raced against
      // MATCH_END and could fire handleGameOver() a frame early, before the
      // server's real match stats (js/engine/stats.js MatchStats.serialize())
      // had even arrived - locking in an empty Gamerank/MVP display for the
      // rest of the match's re-entrancy-guarded life.
    }

    // 2. Authoritative Economy
    if (packet.plasma && eco.resources) {
      eco.resources.western.plasma = packet.plasma.w;
      eco.resources.eastern.plasma = packet.plasma.e;
    }

    // 3. Territory Divider & Dominance Line
    if (packet.divider !== undefined) {
      b.territoryDivider = packet.divider;
      b.territoryControl = packet.control || 'neutral';
      b.territoryBonus = packet.bonus || 0;
    }
    if (packet.isTerritoryLocked !== undefined) {
      b.isTerritoryLocked = !!packet.isTerritoryLocked;
      b.territoryLockedTeam = packet.territoryLockedTeam || null;
    }

    // 4. Hand Cards Synchronization (supports both Western & Eastern on dedicated WS server)
    const myTeam = game.myTeam || game.battlefield?.playerTeam || (packet.hands ? 'western' : 'eastern');
    const myHandList = packet.hands?.[myTeam] || (myTeam === 'eastern' ? packet.guestHand : packet.hostHand);
    if (Array.isArray(myHandList) && cs && cs.hands && cs.hands[myTeam]) {
      for (let s = 0; s < cs.maxHandSize; s++) {
        const expected = myHandList[s];
        if (!expected || expected.name === '···') {
          if (!expected && cs.hands[myTeam][s]) {
            cs.hands[myTeam][s] = null;
          }
          continue;
        }
        const current = cs.hands[myTeam][s];
        if (!current || current.name !== expected.name || (expected.id && current.instanceId !== expected.id)) {
          const cardData = getCardByName(expected.name);
          if (cardData) {
            cs.hands[myTeam][s] = {
              ...cardData,
              slot: s,
              instanceId: expected.id || ('card_' + s)
            };
          }
        }
      }
    }

    // 4a. Opponent Hand Cards Synchronization (supports Hacker Hand Reveal in Online 1v1 / 2v2)
    const oppTeam = myTeam === 'western' ? 'eastern' : 'western';
    const oppHandList = packet.hands?.[oppTeam] || (Array.isArray(packet.opponentHands) ? packet.opponentHands[0] : null);
    if (Array.isArray(oppHandList) && cs && cs.hands) {
      if (!cs.hands[oppTeam]) cs.hands[oppTeam] = new Array(cs.maxHandSize).fill(null);
      const isRevealingUnit = (u) => !u.isDead && (u.isHacker || u.name === 'Hacker' || u.cardData?.unitType === 'Hacker') &&
        u.team === myTeam && u.isHacking && u.hackerTargetId &&
        ((u.handRevealTimer || 0) > 0 || (((u.hackerLockTimer || 0) % 15.0) >= 9.8 && ((u.hackerLockTimer || 0) % 15.0) < 15.0));
      const hasLocalActiveReveal = (b?.units?.some(isRevealingUnit)) || (packet.units?.some(isRevealingUnit));
      for (let s = 0; s < cs.maxHandSize; s++) {
        const expected = oppHandList[s];
        if (!expected) {
          cs.hands[oppTeam][s] = null;
          continue;
        }
        if (expected.name && expected.name !== '···') {
          const current = cs.hands[oppTeam][s];
          if (!current || current.name !== expected.name || (expected.id && current.instanceId !== expected.id)) {
            const cardData = getCardByName(expected.name);
            cs.hands[oppTeam][s] = cardData ? {
              ...cardData,
              slot: s,
              instanceId: expected.id || ('opp_card_' + s)
            } : expected;
          }
        } else {
          const current = cs.hands[oppTeam][s];
          if (hasLocalActiveReveal && current && current.name && current.name !== '···') {
            // Keep revealed card during active local Hacker reveal window
          } else {
            cs.hands[oppTeam][s] = { slot: s, hidden: true, name: '···' };
          }
        }
      }
      if (game.wsMatchClient) {
        game.wsMatchClient.opponentHand = cs.hands[oppTeam];
      }
      if (game.wsClient) {
        game.wsClient.opponentHand = cs.hands[oppTeam];
      }
    }

    // 4b. Card System Events Synchronization (Concentration / Reconsider, Mental Extraction & Melt / Triplet Drilling)
    if (cs) {
      if (!this._initialCardSyncDone) {
        if (typeof packet.concentrationSeq === 'number') cs.concentrationSeq = packet.concentrationSeq;
        if (typeof packet.mentalExtractionSeq === 'number') cs.mentalExtractionSeq = packet.mentalExtractionSeq;
        if (typeof packet.meltSeq === 'number') cs.meltSeq = packet.meltSeq;
        if (game.hud) {
          game.hud.lastSeenConcentrationSeq = cs.concentrationSeq || 0;
          game.hud.lastSeenMentalExtractionSeq = cs.mentalExtractionSeq || 0;
          game.hud.lastSeenMeltSeq = cs.meltSeq || 0;
        }
        this._initialCardSyncDone = true;
      } else {
        if (typeof packet.concentrationSeq === 'number' && packet.concentrationSeq > (cs.concentrationSeq || 0)) {
          cs.concentrationSeq = packet.concentrationSeq;
          if (packet.lastConcentrationSlot) {
            cs.lastConcentrationSlot = packet.lastConcentrationSlot;
          }
        }
        if (typeof packet.mentalExtractionSeq === 'number' && packet.mentalExtractionSeq > (cs.mentalExtractionSeq || 0)) {
          cs.mentalExtractionSeq = packet.mentalExtractionSeq;
          if (packet.lastMentalExtractionSlot) {
            cs.lastMentalExtractionSlot = packet.lastMentalExtractionSlot;
          }
        }
        if (typeof packet.meltSeq === 'number' && packet.meltSeq > (cs.meltSeq || 0)) {
          cs.meltSeq = packet.meltSeq;
          if (packet.lastMeltSlot) {
            cs.lastMeltSlot = packet.lastMeltSlot;
          }
        }
      }
    }

    // 4b. Precision Strikes Reconciliation
    if (Array.isArray(packet.precisionStrikes)) {
      if (!b.activePrecisionStrikes) b.activePrecisionStrikes = [];
      if (packet.precisionStrikes.length === 0 && b.activePrecisionStrikes.length === 0) {
        // Fast-path: no precision strikes active on either side
      } else {
        const currentStrikes = b.activePrecisionStrikes;
        const strikeIds = this._scratchStrikeIds;
        strikeIds.clear();
        const remoteMap = new Map();
        for (let i = 0; i < packet.precisionStrikes.length; i++) {
          const rs = packet.precisionStrikes[i];
          remoteMap.set(rs.id, rs);
          strikeIds.add(rs.id);
        }
        for (let i = currentStrikes.length - 1; i >= 0; i--) {
          const cs = currentStrikes[i];
          const rs = remoteMap.get(cs.id);
          if (!rs || rs.isResolved) {
            currentStrikes.splice(i, 1);
          } else {
            cs.state = rs.state;
            cs.targetUnitId = rs.targetUnitId || null;
            cs.targetX = rs.targetX;
            cs.targetY = rs.targetY;
            if (Math.abs(cs.shadowX - rs.shadowX) > 100) {
              cs.shadowX = rs.shadowX;
            }
            cs.shadowDir = rs.shadowDir;
            cs.shadowSpeed = rs.shadowSpeed;
            cs.shadowY = rs.shadowY;
            cs.isBombSpawned = !!rs.isBombSpawned;
            cs.bombAltitude = rs.bombAltitude;
            cs.bombDuration = rs.bombDuration;
            cs.isResolved = !!rs.isResolved;
            remoteMap.delete(cs.id);
          }
        }
        for (const [id, rs] of remoteMap) {
          if (!rs.isResolved) {
            currentStrikes.push({
              id: rs.id,
              team: rs.team,
              enemyTeam: rs.enemyTeam,
              state: rs.state,
              targetUnitId: rs.targetUnitId || null,
              targetX: rs.targetX,
              targetY: rs.targetY,
              shadowX: rs.shadowX,
              shadowDir: rs.shadowDir,
              shadowSpeed: rs.shadowSpeed,
              shadowY: rs.shadowY,
              isBombSpawned: !!rs.isBombSpawned,
              bombAltitude: rs.bombAltitude,
              bombDuration: rs.bombDuration,
              isResolved: !!rs.isResolved
            });
          }
        }
      }
    }

    // 5. Units Reconciliation & Smooth Position Interpolation
    if (Array.isArray(packet.units)) {
      const remoteMap = this._remoteMapScratch;
      remoteMap.clear();
      for (let i = 0; i < packet.units.length; i++) {
        const ru = packet.units[i];
        remoteMap.set(ru.id, ru);
      }

      // Reconcile existing units
      const myTeam = game?.myTeam || 'western';
      for (let i = b.units.length - 1; i >= 0; i--) {
        const u = b.units[i];
        const ru = remoteMap.get(u.id);
        if (!ru) {
          // Cloak grace period (M8): if an enemy cloaked unit temporarily loses detection,
          // don't immediately prune and recreate it on detector flicker.
          // Wait up to 1.5s for detector re-acquisition before treating as dead.
          if (u.isCloaked && u.team !== myTeam && !u.isDead) {
            if (u._cloakGraceTimer === undefined) {
              u._cloakGraceTimer = 1.5;
            } else {
              // Snapshots are sent at 30 Hz; decrement by real snapshot delta (~1/30 s)
              // instead of 0.1 s per packet so that the intended 1.5 s grace holds.
              u._cloakGraceTimer -= (1.0 / 30.0);
            }
            if (u._cloakGraceTimer > 0) {
              u._cloakUndetected = true;
              continue;
            }
          }
          // Unit died or was removed on the Host (silent cleanup without false death explosions)
          u.isDead = true;
          b.units.splice(i, 1);
          b.unitMap.delete(u.id);
          if (u.isStructure && b.structures) {
            const sIdx = b.structures.indexOf(u);
            if (sIdx !== -1) b.structures.splice(sIdx, 1);
          }
          if (u.isDetector && b.detectors?.[u.team]) {
            const dIdx = b.detectors[u.team].indexOf(u);
            if (dIdx !== -1) b.detectors[u.team].splice(dIdx, 1);
          }
          if (b.goldenStatues) {
            const gIdx = b.goldenStatues.indexOf(u);
            if (gIdx !== -1) b.goldenStatues.splice(gIdx, 1);
          }
          if (b.cocoons) {
            const cIdx = b.cocoons.indexOf(u);
            if (cIdx !== -1) b.cocoons.splice(cIdx, 1);
          }
          // Stop any active continuous vehicle/ambience loop when the last unit of that type leaves the field
          const uCard = u.cardData || getCardByName(u.name);
          const uName = uCard?.name || u.name;
          const uType = uCard?.unitType;
          if (uName === 'Wheelie Bomber' || uType === 'WheelieBomber') {
            const stillHasWheelie = b.units.some(other => !other.isDead && (other.name === 'Wheelie Bomber' || other.cardData?.unitType === 'WheelieBomber'));
            if (!stillHasWheelie) rawAudio?.stopWheelieEngine?.();
          } else if (uName === 'A9 Piranha' || uType === 'A9Piranha') {
            const stillHasPiranha = b.units.some(other => !other.isDead && (other.name === 'A9 Piranha' || other.cardData?.unitType === 'A9Piranha'));
            if (!stillHasPiranha) rawAudio?.stopA9PiranhaMotorLoop?.();
          } else if (u.isAntiShade || uName === 'Anti Shade' || uType === 'AntiShade') {
            const stillHasAntiShade = b.units.some(other => !other.isDead && (other.isAntiShade || other.name === 'Anti Shade' || other.cardData?.unitType === 'AntiShade'));
            if (!stillHasAntiShade) rawAudio?.stopAntiShadeBreath?.();
          }
        } else {
          // Unit alive: smooth position lerp & sync vitals
          u._cloakGraceTimer = undefined;
          u._cloakUndetected = false;
          if (ru.team && ru.team !== u.team) {
            if (typeof b.transferUnit === 'function') {
              b.transferUnit(u, ru.team);
            } else {
              u.team = ru.team;
            }
          }
          u.hp = ru.hp;
          u.maxHp = ru.maxHp || u.maxHp;
          u.isAir = ru.isAir;
          u.isCloaked = ru.isCloaked;
          u.decloakTimer = ru.decloakTimer || 0;
          u.targetX = ru.x;
          u.targetY = ru.y;
          u.targetId = ru.targetId || null;
          if (ru.attackCooldown !== undefined) u.attackCooldown = ru.attackCooldown;
          if (ru.isAttacking !== undefined) u.isAttacking = ru.isAttacking;
          if (ru.speed !== undefined) u.speed = ru.speed;
          if (u.materializeTimer > 0) u.materializeTimer = 0;
          if (ru.isParachuting !== undefined) u.isParachuting = ru.isParachuting;
          if (ru.parachuteTimer !== undefined) u.parachuteTimer = ru.parachuteTimer;
          if (ru.parachuteAltitude !== undefined) u.parachuteAltitude = ru.parachuteAltitude;
          if (ru.burstTimer !== undefined) u.burstTimer = ru.burstTimer;
          if (ru.isReloading !== undefined) u.isReloading = ru.isReloading;
          if (ru.rocketsFired !== undefined) u.rocketsFired = ru.rocketsFired;
          if (ru.deployProgress !== undefined) u.deployProgress = ru.deployProgress;
          if (ru.underFireTimer !== undefined) u.underFireTimer = ru.underFireTimer;
          if (ru.smokeCloakTimer !== undefined) {
            u.smokeCloakTimer = ru.smokeCloakTimer;
            u.deflectionBonus = u.smokeCloakTimer > 0 ? 0.6 : 0;
          }
          if (ru.smokeGrenadeTriggered !== undefined) u.smokeGrenadeTriggered = ru.smokeGrenadeTriggered;
          if (ru.executionTimer !== undefined) {
            u.executionTimer = ru.executionTimer;
            u.isUntargetable = u.executionTimer > 0;
          }
          if (ru.executionTargetId !== undefined) u.executionTargetId = ru.executionTargetId;
          if (ru.executionCooldown !== undefined) u.executionCooldown = ru.executionCooldown;
          if (ru.facingDir !== undefined) u.facingDir = ru.facingDir;
          if (ru.isPincerStalking !== undefined) u.isPincerStalking = ru.isPincerStalking;
          if (ru.isHeld !== undefined) u.isHeld = ru.isHeld;
          if (ru.edition !== undefined) u.edition = ru.edition;
          if (ru.customAuraHex !== undefined) {
            u.customAuraHex = ru.customAuraHex;
            u._customAuraHex = ru.customAuraHex || null;
          }
          if (ru.tetherTargetId !== undefined) u.tetherTargetId = ru.tetherTargetId;
          if (ru.beamTargetId !== undefined) u.beamTargetId = ru.beamTargetId;
          if (ru.dualTetherTarget1Id !== undefined) u.dualTetherTarget1Id = ru.dualTetherTarget1Id;
          if (ru.dualTetherTarget2Id !== undefined) u.dualTetherTarget2Id = ru.dualTetherTarget2Id;
          if (ru.tether1LockTimer !== undefined) u.tether1LockTimer = ru.tether1LockTimer;
          if (ru.isRooted !== undefined) u.isRooted = ru.isRooted;
          if (ru.acidStacks !== undefined) u.acidStacks = ru.acidStacks;
          if (ru.plasmaChargeTimer !== undefined) u.plasmaChargeTimer = ru.plasmaChargeTimer;
          if (ru.plasmaMorphed !== undefined) u.plasmaMorphed = !!ru.plasmaMorphed;
          if (ru.plasmaTargetIsAir !== undefined) u.plasmaTargetIsAir = !!ru.plasmaTargetIsAir;
          if (ru.isGoldenStatue !== undefined) {
            u.isGoldenStatue = !!ru.isGoldenStatue;
            if (u.isGoldenStatue) {
              u.speed = 0;
              u.canAttack = false;
              u.isUntargetable = true;
              u.untargetable = true;
              u.mass = Infinity;
              if (b?.goldenStatues && !b.goldenStatues.includes(u)) {
                b.goldenStatues.push(u);
              }
            }
          }
          if (ru.isFallingStatue !== undefined) u.isFallingStatue = !!ru.isFallingStatue;
          if (ru.isCocoon !== undefined) {
            u.isCocoon = !!ru.isCocoon;
            if (u.isCocoon) {
              u.speed = 0;
              u.canAttack = false;
              u.isUntargetable = false;
              u.untargetable = false;
              u.mass = Infinity;
              if (b?.cocoons && !b.cocoons.includes(u)) {
                b.cocoons.push(u);
              }
            }
          }
          if (ru.cocoonTimer !== undefined) u.cocoonTimer = ru.cocoonTimer;
          if (ru.isFallingCocoon !== undefined) u.isFallingCocoon = !!ru.isFallingCocoon;
          if (ru.hasPetrifiedUnit !== undefined) u.hasPetrifiedUnit = !!ru.hasPetrifiedUnit;
          if (ru.hasGoldPedestal !== undefined) u.hasGoldPedestal = !!ru.hasGoldPedestal;
          if (ru.hasParasite !== undefined) u.hasParasite = !!ru.hasParasite;
          if (ru.isInsane !== undefined) u.isInsane = !!ru.isInsane;
          if (ru.isParasitized !== undefined) u.isParasitized = !!ru.isParasitized;
          if (ru.overstimulationTimer !== undefined) u.overstimulationTimer = ru.overstimulationTimer;
          if (ru.isOverstimulated !== undefined) u.isOverstimulated = !!ru.isOverstimulated;
          if (ru.isRetreating !== undefined) u.isRetreating = !!ru.isRetreating;
          if (ru.isBastionRearGuard !== undefined) u.isBastionRearGuard = !!ru.isBastionRearGuard;
          if (ru.isPriorityTarget !== undefined) u.isPriorityTarget = !!ru.isPriorityTarget;
          if (ru.isMineRising !== undefined) u.isMineRising = !!ru.isMineRising;
          if (ru.mineRiseTimer !== undefined) u.mineRiseTimer = ru.mineRiseTimer;
          if (ru.isBurrowing !== undefined) u.isBurrowing = !!ru.isBurrowing;
          if (ru.burrowTimer !== undefined) u.burrowTimer = ru.burrowTimer;
          if (ru.isBurrowed !== undefined) u.isBurrowed = !!ru.isBurrowed;
          if (ru.mineState !== undefined) u.mineState = ru.mineState;
          if (ru.wasBurrowedBeforeRising !== undefined) u.wasBurrowedBeforeRising = !!ru.wasBurrowedBeforeRising;
          if (ru.burrowAnchorX !== undefined) u.burrowAnchorX = ru.burrowAnchorX;
          if (ru.burrowAnchorY !== undefined) u.burrowAnchorY = ru.burrowAnchorY;
          if (ru.sporeSlowTimer !== undefined) u.sporeSlowTimer = ru.sporeSlowTimer;
          if (ru.sporeSlowPenalty !== undefined) u.sporeSlowPenalty = ru.sporeSlowPenalty;
          if (ru.disarmTimer !== undefined) u.disarmTimer = ru.disarmTimer;
          if (ru.isGrowing !== undefined) u.isGrowing = ru.isGrowing;
          if (ru.growthTimer !== undefined) u.growthTimer = ru.growthTimer;
          if (ru.snapStrikeTimer !== undefined) u.snapStrikeTimer = ru.snapStrikeTimer;
          if (ru.snapTargetId !== undefined) u.snapTargetId = ru.snapTargetId;
          if (ru.firstHitDone !== undefined) u.firstHitDone = ru.firstHitDone;
          if (ru.hasDealtFirstHit !== undefined) u.hasDealtFirstHit = ru.hasDealtFirstHit;
          if (ru.pinkBeretState !== undefined) u.pinkBeretState = ru.pinkBeretState;
          if (ru.pinkBeretAimTimer !== undefined) u.pinkBeretAimTimer = ru.pinkBeretAimTimer;
          if (ru.pinkBeretTransitionTimer !== undefined) u.pinkBeretTransitionTimer = ru.pinkBeretTransitionTimer;
          if (ru.pinkBeretTargetId !== undefined) u.pinkBeretTargetId = ru.pinkBeretTargetId;
          if (ru.hasFiredInitialRocket !== undefined) u.hasFiredInitialRocket = ru.hasFiredInitialRocket;
          if (ru.isStoppedAtBase !== undefined) u.isStoppedAtBase = ru.isStoppedAtBase;
          if (ru.hackerState !== undefined) u.hackerState = ru.hackerState;
          if (ru.hackerTargetId !== undefined) u.hackerTargetId = ru.hackerTargetId;
          if (ru.hackerLockTimer !== undefined) u.hackerLockTimer = ru.hackerLockTimer;
          if (ru.isHacking !== undefined) u.isHacking = ru.isHacking;
          if (ru.isTranscendent !== undefined) u.isTranscendent = ru.isTranscendent;
          if (ru.transcendentTimer !== undefined) u.transcendentTimer = ru.transcendentTimer;
          if (ru.handRevealTimer !== undefined) u.handRevealTimer = ru.handRevealTimer;
          if (ru.isDisabled !== undefined) {
            const wasDisabled = u.isDisabled;
            u.isDisabled = !!ru.isDisabled;
            if (wasDisabled && !u.isDisabled && typeof u.reactivateFromHack === 'function') {
              u.reactivateFromHack();
            }
          }
          if (ru.disabledByHackerId !== undefined) u.disabledByHackerId = ru.disabledByHackerId;
          if (ru.isIndestructible !== undefined) u.isIndestructible = ru.isIndestructible;
          if (ru._hpRegenFlash !== undefined) u._hpRegenFlash = ru._hpRegenFlash;
          if (ru.isSentinelIdle !== undefined) u.isSentinelIdle = !!ru.isSentinelIdle;
          if (ru.turretAimState !== undefined) u.turretAimState = ru.turretAimState;
          if (ru.hasBomb !== undefined) u.hasBomb = !!ru.hasBomb;
          if (ru.pigeonState !== undefined) u.pigeonState = ru.pigeonState;
          if (ru.bombTargetId !== undefined) u.bombTargetId = ru.bombTargetId;
          if (ru.bombHoverTimer !== undefined) u.bombHoverTimer = ru.bombHoverTimer;
          if (ru.isFlyingBackward !== undefined) u.isFlyingBackward = !!ru.isFlyingBackward;
          if (ru.isHovering !== undefined) u.isHovering = !!ru.isHovering;
          if (ru.pigeonPitch !== undefined) u.pigeonPitch = ru.pigeonPitch;
          if (ru.nozzleTilt !== undefined) u.nozzleTilt = ru.nozzleTilt;
          if (Array.isArray(ru.incinerateStacks) && ru.incinerateStacks.length > 0) {
            if (!u.incinerateStacks) u.incinerateStacks = [];
            const rLen = ru.incinerateStacks.length;
            for (let sIdx = 0; sIdx < rLen; sIdx++) {
              const src = ru.incinerateStacks[sIdx];
              let target = u.incinerateStacks[sIdx];
              if (!target) {
                target = { duration: 0, remaining: 0, tickTimer: 0.5, dps: 2, attackerTeam: null };
                u.incinerateStacks[sIdx] = target;
              }
              target.duration = src.duration || src.remaining;
              target.remaining = src.remaining;
              target.tickTimer = src.tickTimer !== undefined ? src.tickTimer : 0.5;
              target.dps = src.dps || 2;
              target.attackerTeam = src.attackerTeam || null;
            }
            u.incinerateStacks.length = rLen;
          } else if (u.incinerateStacks && u.incinerateStacks.length > 0) {
            u.incinerateStacks.length = 0;
          }
          if (typeof ru.age === 'number') u.age = ru.age;

          const dx = ru.x - u.x;
          const dy = ru.y - u.y;

          if (dx * dx + dy * dy > 3600) {
            // Large displacement (e.g. initial spawn, blink, or severe desync) -> hard snap
            u.x = ru.x;
            u.y = ru.y;
          }
          // Note: Continuous smooth motion is handled by updateInterpolation() every frame at 60 FPS,
          // completely avoiding the periodic 30 Hz impulse jerk caused by snapping on packet arrival.

          remoteMap.delete(u.id);
        }
      }

      // Spawn any brand-new units received from Host
      const shouldPlaySpawnAudio = this._initialUnitSyncDone;
      for (const [id, ru] of remoteMap) {
        const cardData = getCardByName(ru.name);
        if (cardData) {
          const newUnit = b.spawnRaw(cardData, ru.team, ru.x, ru.y, {
            id: ru.id,
            edition: ru.edition || null,
            customAuraHex: ru.customAuraHex || null,
            materializeDuration: 0,
            materializeTimer: 0
          });
          newUnit.targetX = ru.x;
          newUnit.targetY = ru.y;
          newUnit.targetId = ru.targetId || null;
          if (ru.attackCooldown !== undefined) newUnit.attackCooldown = ru.attackCooldown;
          if (ru.isAttacking !== undefined) newUnit.isAttacking = ru.isAttacking;
          if (ru.speed !== undefined) newUnit.speed = ru.speed;
          newUnit.hp = ru.hp;
          newUnit.maxHp = ru.maxHp || newUnit.maxHp;
          newUnit.isAir = ru.isAir;
          newUnit.isCloaked = ru.isCloaked;
          newUnit.decloakTimer = ru.decloakTimer || 0;
          if (ru.isParachuting !== undefined) newUnit.isParachuting = ru.isParachuting;
          if (ru.parachuteTimer !== undefined) newUnit.parachuteTimer = ru.parachuteTimer;
          if (ru.parachuteAltitude !== undefined) newUnit.parachuteAltitude = ru.parachuteAltitude;
          if (ru.burstTimer !== undefined) newUnit.burstTimer = ru.burstTimer;
          if (ru.isReloading !== undefined) newUnit.isReloading = ru.isReloading;
          if (ru.rocketsFired !== undefined) newUnit.rocketsFired = ru.rocketsFired;
          if (ru.deployProgress !== undefined) newUnit.deployProgress = ru.deployProgress;
          if (ru.underFireTimer !== undefined) newUnit.underFireTimer = ru.underFireTimer;
          if (ru.smokeCloakTimer !== undefined) {
            newUnit.smokeCloakTimer = ru.smokeCloakTimer;
            newUnit.deflectionBonus = newUnit.smokeCloakTimer > 0 ? 0.6 : 0;
          }
          if (ru.smokeGrenadeTriggered !== undefined) newUnit.smokeGrenadeTriggered = ru.smokeGrenadeTriggered;
          if (ru.executionTimer !== undefined) {
            newUnit.executionTimer = ru.executionTimer;
            newUnit.isUntargetable = newUnit.executionTimer > 0;
          }
          if (ru.executionTargetId !== undefined) newUnit.executionTargetId = ru.executionTargetId;
          if (ru.executionCooldown !== undefined) newUnit.executionCooldown = ru.executionCooldown;
          if (ru.facingDir !== undefined) newUnit.facingDir = ru.facingDir;
          if (ru.isPincerStalking !== undefined) newUnit.isPincerStalking = ru.isPincerStalking;
          if (ru.isUntargetable !== undefined) newUnit.isUntargetable = ru.isUntargetable;
          if (ru.isHeld !== undefined) newUnit.isHeld = ru.isHeld;
          if (ru.isEngaged !== undefined) newUnit.isEngaged = ru.isEngaged;
          if (ru.tetherTargetId !== undefined) newUnit.tetherTargetId = ru.tetherTargetId;
          if (ru.beamTargetId !== undefined) newUnit.beamTargetId = ru.beamTargetId;
          if (ru.dualTetherTarget1Id !== undefined) newUnit.dualTetherTarget1Id = ru.dualTetherTarget1Id;
          if (ru.dualTetherTarget2Id !== undefined) newUnit.dualTetherTarget2Id = ru.dualTetherTarget2Id;
          if (ru.tether1LockTimer !== undefined) newUnit.tether1LockTimer = ru.tether1LockTimer;
          if (ru.isRooted !== undefined) newUnit.isRooted = ru.isRooted;
          if (ru.acidStacks !== undefined) newUnit.acidStacks = ru.acidStacks;
          if (ru.plasmaChargeTimer !== undefined) newUnit.plasmaChargeTimer = ru.plasmaChargeTimer;
          if (ru.plasmaMorphed !== undefined) newUnit.plasmaMorphed = !!ru.plasmaMorphed;
          if (ru.plasmaTargetIsAir !== undefined) newUnit.plasmaTargetIsAir = !!ru.plasmaTargetIsAir;
          if (ru.isGoldenStatue !== undefined) {
            newUnit.isGoldenStatue = !!ru.isGoldenStatue;
            if (newUnit.isGoldenStatue) {
              newUnit.speed = 0;
              newUnit.canAttack = false;
              newUnit.isUntargetable = true;
              newUnit.untargetable = true;
              newUnit.mass = Infinity;
              if (b?.goldenStatues && !b.goldenStatues.includes(newUnit)) {
                b.goldenStatues.push(newUnit);
              }
            }
          }
          if (ru.isFallingStatue !== undefined) newUnit.isFallingStatue = !!ru.isFallingStatue;
          if (ru.isCocoon !== undefined) {
            newUnit.isCocoon = !!ru.isCocoon;
            if (newUnit.isCocoon) {
              newUnit.speed = 0;
              newUnit.canAttack = false;
              newUnit.isUntargetable = false;
              newUnit.untargetable = false;
              newUnit.mass = Infinity;
              if (b?.cocoons && !b.cocoons.includes(newUnit)) {
                b.cocoons.push(newUnit);
              }
            }
          }
          if (ru.cocoonTimer !== undefined) newUnit.cocoonTimer = ru.cocoonTimer;
          if (ru.isFallingCocoon !== undefined) newUnit.isFallingCocoon = !!ru.isFallingCocoon;
          if (ru.hasPetrifiedUnit !== undefined) newUnit.hasPetrifiedUnit = !!ru.hasPetrifiedUnit;
          if (ru.hasGoldPedestal !== undefined) newUnit.hasGoldPedestal = !!ru.hasGoldPedestal;
          if (ru.hasParasite !== undefined) newUnit.hasParasite = !!ru.hasParasite;
          if (ru.isInsane !== undefined) newUnit.isInsane = !!ru.isInsane;
          if (ru.isParasitized !== undefined) newUnit.isParasitized = !!ru.isParasitized;
          if (ru.overstimulationTimer !== undefined) newUnit.overstimulationTimer = ru.overstimulationTimer;
          if (ru.isOverstimulated !== undefined) newUnit.isOverstimulated = !!ru.isOverstimulated;
          if (ru.isRetreating !== undefined) newUnit.isRetreating = !!ru.isRetreating;
          if (ru.isBastionRearGuard !== undefined) newUnit.isBastionRearGuard = !!ru.isBastionRearGuard;
          if (ru.isPriorityTarget !== undefined) newUnit.isPriorityTarget = !!ru.isPriorityTarget;
          if (ru.isMineRising !== undefined) newUnit.isMineRising = !!ru.isMineRising;
          if (ru.mineRiseTimer !== undefined) newUnit.mineRiseTimer = ru.mineRiseTimer;
          if (ru.isBurrowing !== undefined) newUnit.isBurrowing = !!ru.isBurrowing;
          if (ru.burrowTimer !== undefined) newUnit.burrowTimer = ru.burrowTimer;
          if (ru.isBurrowed !== undefined) newUnit.isBurrowed = !!ru.isBurrowed;
          if (ru.mineState !== undefined) newUnit.mineState = ru.mineState;
          if (ru.wasBurrowedBeforeRising !== undefined) newUnit.wasBurrowedBeforeRising = !!ru.wasBurrowedBeforeRising;
          if (ru.burrowAnchorX !== undefined) newUnit.burrowAnchorX = ru.burrowAnchorX;
          if (ru.burrowAnchorY !== undefined) newUnit.burrowAnchorY = ru.burrowAnchorY;
          if (ru.sporeSlowTimer !== undefined) newUnit.sporeSlowTimer = ru.sporeSlowTimer;
          if (ru.sporeSlowPenalty !== undefined) newUnit.sporeSlowPenalty = ru.sporeSlowPenalty;
          if (ru.disarmTimer !== undefined) newUnit.disarmTimer = ru.disarmTimer;
          if (ru.isGrowing !== undefined) newUnit.isGrowing = ru.isGrowing;
          if (ru.growthTimer !== undefined) newUnit.growthTimer = ru.growthTimer;
          if (ru.snapStrikeTimer !== undefined) newUnit.snapStrikeTimer = ru.snapStrikeTimer;
          if (ru.snapTargetId !== undefined) newUnit.snapTargetId = ru.snapTargetId;
          if (ru.firstHitDone !== undefined) newUnit.firstHitDone = ru.firstHitDone;
          if (ru.hasDealtFirstHit !== undefined) newUnit.hasDealtFirstHit = ru.hasDealtFirstHit;
          if (ru.pinkBeretState !== undefined) newUnit.pinkBeretState = ru.pinkBeretState;
          if (ru.pinkBeretAimTimer !== undefined) newUnit.pinkBeretAimTimer = ru.pinkBeretAimTimer;
          if (ru.pinkBeretTransitionTimer !== undefined) newUnit.pinkBeretTransitionTimer = ru.pinkBeretTransitionTimer;
          if (ru.pinkBeretTargetId !== undefined) newUnit.pinkBeretTargetId = ru.pinkBeretTargetId;
          if (ru.hasFiredInitialRocket !== undefined) newUnit.hasFiredInitialRocket = ru.hasFiredInitialRocket;
          if (ru.isStoppedAtBase !== undefined) newUnit.isStoppedAtBase = ru.isStoppedAtBase;
          if (ru.hackerState !== undefined) newUnit.hackerState = ru.hackerState;
          if (ru.hackerTargetId !== undefined) newUnit.hackerTargetId = ru.hackerTargetId;
          if (ru.hackerLockTimer !== undefined) newUnit.hackerLockTimer = ru.hackerLockTimer;
          if (ru.isHacking !== undefined) newUnit.isHacking = ru.isHacking;
          if (ru.isTranscendent !== undefined) newUnit.isTranscendent = ru.isTranscendent;
          if (ru.transcendentTimer !== undefined) newUnit.transcendentTimer = ru.transcendentTimer;
          if (ru.handRevealTimer !== undefined) newUnit.handRevealTimer = ru.handRevealTimer;
          if (ru.isDisabled !== undefined) newUnit.isDisabled = ru.isDisabled;
          if (ru.disabledByHackerId !== undefined) newUnit.disabledByHackerId = ru.disabledByHackerId;
          if (ru.isIndestructible !== undefined) newUnit.isIndestructible = ru.isIndestructible;
          if (ru._hpRegenFlash !== undefined) newUnit._hpRegenFlash = ru._hpRegenFlash;
          if (ru.isSentinelIdle !== undefined) newUnit.isSentinelIdle = !!ru.isSentinelIdle;
          if (ru.turretAimState !== undefined) newUnit.turretAimState = ru.turretAimState;
          if (ru.hasBomb !== undefined) newUnit.hasBomb = !!ru.hasBomb;
          if (ru.pigeonState !== undefined) newUnit.pigeonState = ru.pigeonState;
          if (ru.bombTargetId !== undefined) newUnit.bombTargetId = ru.bombTargetId;
          if (ru.bombHoverTimer !== undefined) newUnit.bombHoverTimer = ru.bombHoverTimer;
          if (ru.isFlyingBackward !== undefined) newUnit.isFlyingBackward = !!ru.isFlyingBackward;
          if (ru.isHovering !== undefined) newUnit.isHovering = !!ru.isHovering;
          if (ru.pigeonPitch !== undefined) newUnit.pigeonPitch = ru.pigeonPitch;
          if (ru.nozzleTilt !== undefined) newUnit.nozzleTilt = ru.nozzleTilt;
          if (Array.isArray(ru.incinerateStacks) && ru.incinerateStacks.length > 0) {
            newUnit.incinerateStacks = ru.incinerateStacks.map(s => ({
              duration: s.duration || s.remaining,
              remaining: s.remaining,
              tickTimer: s.tickTimer !== undefined ? s.tickTimer : 0.5,
              dps: s.dps || 2,
              attackerTeam: s.attackerTeam || null
            }));
          } else {
            newUnit.incinerateStacks = [];
          }
          if (typeof ru.age === 'number') newUnit.age = ru.age;

          // Acoustic & visual spawn parity for online matches
          if (shouldPlaySpawnAudio && (ru.age === undefined || ru.age < 1.0)) {
            const isFriendly = (ru.team === game.myTeam || ru.team === game.myRole || (game.is2v2 && ((game.myTeam === 'western' && (ru.team === 'w1' || ru.team === 'w2')) || (game.myTeam === 'eastern' && (ru.team === 'e1' || ru.team === 'e2')))));
            if (isFriendly) {
              if (cardData.name === 'The Salvage Company' || cardData.unitType === 'TheSalvageCompany') {
                rawAudio?.playSalvageHorn?.();
              } else if (!hasCustomSpawnSound(cardData)) {
                rawAudio?.playUnitSpawn?.(cardData, { x: ru.x });
              }
            } else {
              // Enemy boss unit horn audible across lane
              if (cardData.name === 'The Salvage Company' || cardData.unitType === 'TheSalvageCompany') {
                rawAudio?.playSalvageHorn?.({ volume: 0.6 });
              }
            }

            // Custom physical drive/motor/ambience spawn sounds (both friendly and enemy)
            if (cardData.name === 'Wheelie Bomber' || cardData.unitType === 'WheelieBomber') {
              rawAudio?.playWheelieEngine?.(0.7, { x: ru.x });
            } else if (cardData.name === 'A9 Piranha' || cardData.unitType === 'A9Piranha') {
              rawAudio?.startA9PiranhaMotorLoop?.();
            } else if (newUnit.isAntiShade || cardData.name === 'Anti Shade' || cardData.unitType === 'AntiShade') {
              rawAudio?.playAntiShadeBreath?.();
            }

            // Special-edition drop-in VFX & SFX
            if (ru.edition === 'gold') {
              const groundPointY = ru.y + (newUnit.isAir ? 24 : 6);
              particles?.addDropInEffect?.(ru.x, groundPointY, newUnit.radius || 20, 'gold');
              rawAudio?.playGoldDropIn?.({ x: ru.x });
            }
          }
        }
      }
      this._initialUnitSyncDone = true;
    }

    // 6. Frame Audio & Particle Event Dispatching
    if (Array.isArray(packet.events)) {
      // Compound-key audio burst limiter (see wrapAudioBurstLimiter() above) -
      // shadows the outer `audio`-shaped `rawAudio` binding for the rest of
      // this block only, so every `audio?.playXxx?.()` call below is gated
      // without touching any of the per-unit-type dispatch logic itself.
      this._audioBurstMap.clear();
      if (this._gatedAudioSource !== rawAudio) {
        this._gatedAudioSource = rawAudio;
        this._gatedAudioCache = wrapAudioBurstLimiter(rawAudio, this._audioBurstMap);
      }
      const audio = this._gatedAudioCache;
      for (let i = 0; i < packet.events.length; i++) {
        const ev = packet.events[i];
        if (ev.type === 'SOUND' && ev.name) {
          audio?.[ev.name]?.();
        } else if (ev.type === 'ATTACK') {
          const uType = ev.unitType;
          const race = ev.race;
          if (ev.projType === 'Rocket') {
            if (uType === 'Orchestra') {
              audio?.playRocketLaunch?.('heavy', { x: ev.x, unitType: 'Orchestra' });
              let launchY = ev.y;
              const orchAttacker = ev.attackerUnitId != null ? b?.getUnitById?.(ev.attackerUnitId) : null;
              if (orchAttacker && Math.abs(ev.y - orchAttacker.y) < 5) {
                launchY = orchAttacker.y - 32;
              } else if (!orchAttacker && Math.abs(ev.y - ev.ty) < 5) {
                launchY = ev.y - 32;
              }
              particles?.addMuzzleFlash?.(ev.x, launchY, ev.customColor || '#ffffff', 14);
              particles?.addRisingEmbers?.(ev.x, launchY, 4, 12);
              if (particles?.addOrchestraRocket) {
                particles.addOrchestraRocket(ev.x, launchY, { x: ev.tx, y: ev.ty }, ev.customColor || '#ff8800', 480, null, ev.customRocketTrail);
              } else {
                particles?.addProjectile?.(ev.x, launchY, { x: ev.tx, y: ev.ty }, ev.customColor || '#ff8800', 5, 480);
              }
            } else if (uType === 'PinkBeret' || uType === 'Pink Beret') {
              audio?.playRocketLaunch?.('heavy', { x: ev.x, unitType: 'PinkBeret' });
              const trailColor = ev.customRocketTrail || '#ff2a85';
              particles?.addMuzzleFlash?.(ev.x, ev.y, trailColor, 10);
              particles?.addRisingEmbers?.(ev.x, ev.y, 4, 12);
              if (!packet.events.some(e => e.type === 'PINK_BERET_ROCKET')) {
                const liveTarget = (ev.targetUnitId != null) ? b?.getUnitById?.(ev.targetUnitId) : null;
                const targetRef = liveTarget || { x: ev.tx, y: ev.ty };
                if (particles?.addPinkBeretHomingRocket) {
                  particles.addPinkBeretHomingRocket(ev.x, ev.y, targetRef, 480, null, trailColor);
                } else {
                  particles?.addProjectile?.(ev.x, ev.y, { x: ev.tx, y: ev.ty }, trailColor, 5, 480);
                }
              }
            } else {
              const rocketColor = ev.customColor || (race === 'Sul' ? '#c084fc' : '#ff8800');
              audio?.playRocketLaunch?.('normal', uType === 'Rocketship' ? { volume: 0.7 } : {});
              particles?.addMuzzleFlash?.(ev.x, ev.y, rocketColor, 8);
              particles?.addRisingEmbers?.(ev.x, ev.y, 3, 10);
              if (uType === 'Rocketship' && particles?.addDualCurvedMissiles) {
                particles.addDualCurvedMissiles(ev.x, ev.y, { x: ev.tx, y: ev.ty }, rocketColor, 5, 480);
              } else {
                particles?.addProjectile?.(ev.x, ev.y, { x: ev.tx, y: ev.ty }, rocketColor, 5, 480);
              }
            }
          } else if (race === 'Human') {
            if (uType === 'PinkBeret' || uType === 'Pink Beret') {
              audio?.playGauss?.({ x: ev.x });
              const bulletColor = ev.customColor || '#ff2a85';
              particles?.addMuzzleFlash?.(ev.x, ev.y, bulletColor, 5);
              particles?.addShellCasing?.(ev.x, ev.y, ev.team === 'western' ? 1 : -1);
              particles?.addTracer?.(ev.x, ev.y, ev.tx, ev.ty, bulletColor);
              particles?.addAnamorphicFlare?.(ev.x, ev.y, 70, 2.0, 0.10, '#ff69b4');
            } else if (uType === 'HyperPanzer' || uType === 'Hyper Panzer') {
              audio?.playExplosion?.('large');
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#ff5500', 14);
              particles?.addAnamorphicFlare?.(ev.x, ev.y, 180, 4.5, 0.20, '#fbbf24');
              particles?.addDistortionRing?.(ev.x, ev.y, 50, 0.30, 8);
              particles?.addShockwave?.(ev.x, ev.y, 28, '#f97316', 0.18);
              particles?.addScreenShake?.(5.0, 0.18);
              particles?.addProjectile?.(ev.x, ev.y, { x: ev.tx, y: ev.ty }, '#ff7700', 7, 620);
            } else if (uType === 'Stickman') {
              audio?.playExplosion?.('small');
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#ffaa00', 8);
              particles?.addAnamorphicFlare?.(ev.x, ev.y, 110, 2.5, 0.12, '#fbbf24');
              if (particles?.addBallisticBaseShell) {
                particles.addBallisticBaseShell(ev.x, ev.y, ev.tx, ev.ty, null, ev.customColor || '#c9c96b');
              } else {
                particles?.addProjectile?.(ev.x, ev.y, { x: ev.tx, y: ev.ty }, ev.customColor || '#ffaa00', 5, 420);
              }
            } else if (uType === 'HotHand' || uType === 'Hot Hand' || ev.projType === 'Melee') {
              particles?.addHitSparks?.(ev.tx, ev.ty, 'sparks', 5);
            } else if (uType === 'A9Piranha') {
              audio?.playA9PiranhaShot ? audio.playA9PiranhaShot({ x: ev.x }) : audio?.playGauss?.();
              particles?.addMuzzleFlash?.(ev.x, ev.y - 2.5, '#facc15', 5);
              particles?.addMuzzleFlash?.(ev.x, ev.y + 2.5, '#facc15', 5);
              particles?.addTracer?.(ev.x, ev.y - 2.5, ev.tx, ev.ty, '#ffdd44');
              particles?.addTracer?.(ev.x, ev.y + 2.5, ev.tx, ev.ty, '#ffdd44');
            } else if (uType === 'A9Pigeon' || uType === 'A9 Pigeon') {
              if (audio?.playA9PigeonShot) {
                audio.playA9PigeonShot({ x: ev.x });
              } else {
                audio?.playGauss?.({ x: ev.x, unitType: 'A9Pigeon' });
              }
              const bulletColor = ev.customColor || '#ffdd44';
              particles?.addMuzzleFlash?.(ev.x, ev.y, bulletColor, 5);
              particles?.addShellCasing?.(ev.x, ev.y, ev.team === 'western' ? 1 : -1);
              particles?.addTracer?.(ev.x, ev.y, ev.tx, ev.ty, bulletColor);
            } else if (uType === 'A9Grizzly' || uType === 'A9 Grizzly') {
              if (audio?.playA9GrizzlyShot) {
                audio.playA9GrizzlyShot({ x: ev.x });
              } else if (audio?.playUnitSound) {
                audio.playUnitSound('A9Grizzly', 'attack', { x: ev.x });
              } else {
                audio?.playGauss?.({ x: ev.x, unitType: 'A9Grizzly' });
              }
              const bulletColor = ev.customColor || '#ffdd44';
              particles?.addMuzzleFlash?.(ev.x, ev.y, bulletColor, 5);
              particles?.addShellCasing?.(ev.x, ev.y, ev.team === 'western' ? 1 : -1);
              particles?.addTracer?.(ev.x, ev.y, ev.tx, ev.ty, bulletColor);
              particles?.addAnamorphicFlare?.(ev.x, ev.y, 70, 2.0, 0.10, ev.customColor || '#fef08a');
            } else if (uType === 'Marine') {
              audio?.playGauss?.();
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#facc15', 5);
              particles?.addTracer?.(ev.x, ev.y, ev.tx, ev.ty, '#ffdd44');
            } else if (uType === 'Shade' || uType === 'HyperShade') {
              // Direkter Nutzer-Fund (docs/SOUND_BATCH_PLAN.md section 1.1),
              // gespiegelt aus js/engine/unit.js - Non-Host-Client-Replay
              // muss denselben Sound hören wie die Host-Simulation.
              audio?.playSuppressedShot?.({ x: ev.x });
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#facc15', 5);
              particles?.addTracer?.(ev.x, ev.y, ev.tx, ev.ty, '#ffdd44');
            } else if (uType === 'DevilPatrol') {
              audio?.playExplosion?.('small');
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#ff4400', 8);
              particles?.addFire?.(ev.x, ev.y, ev.tx, ev.ty);
            } else if (uType === 'HyperDevilPatrol') {
              audio?.playHyperDevilShot ? audio.playHyperDevilShot({ x: ev.x }) : audio?.playExplosion?.('small');
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#ff6600', 9);
              particles?.addAnamorphicFlare?.(ev.x, ev.y, 120, 3.0, 0.14, '#fbbf24');
              if (particles?.addBallisticBaseShell) {
                particles.addBallisticBaseShell(ev.x, ev.y, ev.tx, ev.ty, null, ev.customColor || '#c9c96b');
              } else {
                particles?.addProjectile?.(ev.x, ev.y, { x: ev.tx, y: ev.ty }, '#ff6600', 5, 380);
              }
            } else if (uType === 'Panzer') {
              audio?.playExplosion?.('large');
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#ff5500', 12);
              particles?.addShockwave?.(ev.x, ev.y, 25, '#f97316', 0.15);
              particles?.addScreenShake?.(4.5, 0.16);
              particles?.addProjectile?.(ev.x, ev.y, { x: ev.tx, y: ev.ty }, '#ff7700', 6, 600);
            } else if (uType === 'DeployableCannon' || uType === 'Deployable Cannon' || uType === 'Deployable_Cannon') {
              audio?.playExplosion?.('small');
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#ff5500', 8);
              particles?.addAnamorphicFlare?.(ev.x, ev.y, 140, 3.5, 0.16, '#fbbf24');
              if (particles?.addBallisticBaseShell) {
                particles.addBallisticBaseShell(ev.x, ev.y, ev.tx, ev.ty, null, ev.customColor || '#c9c96b');
              } else {
                particles?.addProjectile?.(ev.x, ev.y, { x: ev.tx, y: ev.ty }, ev.customColor || '#ff7700', 5, 520);
              }
            } else if (uType === 'WheelieBomber') {
              // Contact-detonation, not gunfire - matches the non-spectator
              // cosmetic override in Unit.resolveNewCombatAttack().
              audio?.playExplosion?.('heavy');
              particles?.addScreenShake?.(3, 0.16);
              particles?.addExplosion?.(ev.tx, ev.ty, 22, '#ff8800', 12);
            } else {
              audio?.playLaser?.();
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#ffff66', 4);
              particles?.addTracer?.(ev.x, ev.y, ev.tx, ev.ty, '#ffff66');
            }
          } else if (race === 'Sul') {
            if (uType === 'Pulp') {
              audio?.playExplosion?.('small');
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#d946ef', 6);
              if (particles?.addSporeLob) {
                particles.addSporeLob(ev.x, ev.y, ev.tx, ev.ty, null, '#d946ef');
              } else {
                particles?.addProjectile?.(ev.x, ev.y, { x: ev.tx, y: ev.ty }, '#d946ef', 5, 450);
              }
            } else if (uType === 'MonitorLock' || uType === 'Monitor Lock' || ev.projType === 'Snap') {
              if (audio?.playMonitorLockSnap) audio.playMonitorLockSnap();
              else audio?.playGauss?.();
              particles?.addHitSparks?.(ev.tx, ev.ty, 'plasma', 4);
            } else if (uType === 'AntiShade' || uType === 'Anti Shade' || ev.projType === 'Melee') {
              particles?.addHitSparks?.(ev.tx, ev.ty, 'plasma', 4);
            } else if (uType === 'TeslaWalker' || uType === 'HyperTeslaWalker' || uType === 'Tesla Walker' || uType === 'Hyper Tesla Walker') {
              audio?.playTesla ? audio.playTesla() : audio?.playLaser?.();
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#00f0ff', 8);
              if (particles?.addTeslaArc) {
                particles.addTeslaArc(ev.x, ev.y, ev.tx, ev.ty, '#00f0ff');
              } else {
                particles?.addBeam?.(ev.x, ev.y, ev.tx, ev.ty, '#00f0ff', 3.5, 0.16);
              }
            } else if (uType === 'DefectiveLoaf' || uType === 'Defective Loaf') {
              // Melee / tentacle latch unit - damage is applied via tether, hit sparks at target, NO laser sound or tracer
              particles?.addHitSparks?.(ev.tx, ev.ty, 'plasma', 4);
            } else if (uType === 'HarvestingPillar' || uType === 'Harvesting Pillar') {
              // Dual-tentacle bio-siphon structure - no laser sound
              particles?.addHitSparks?.(ev.tx, ev.ty, 'plasma', 6);
            } else if (uType === 'PreserverLock' || uType === 'Preserver Lock') {
              // Continuous plasma beam is rendered off unit.beamTargetId, no laser tracer
              particles?.addHitSparks?.(ev.tx, ev.ty, 'plasma', 3);
            } else if (uType === 'ApostateLoaf' || uType === 'Apostate Loaf') {
              audio?.playExplosion?.('small');
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#84cc16', 6);
              if (particles?.addSporeLob) {
                particles.addSporeLob(ev.x, ev.y, ev.tx, ev.ty, null, '#84cc16');
              } else {
                particles?.addProjectile?.(ev.x, ev.y, { x: ev.tx, y: ev.ty }, '#84cc16', 5, 450);
              }
            } else if (uType === 'SentinalLock' || uType === 'Sentinal Lock') {
              if (audio?.playSentinalSpit) audio.playSentinalSpit();
              else audio?.playHiss?.();
              const globColor = ev.customColor || SENTINAL_LOCK_GLOB_COLOR;
              const lobOptions = getSentinalLobOptions(ev.customColor);
              const mouthY = ev.y + SENTINAL_LOCK_MUZZLE_OFFSET_Y;
              particles?.addMuzzleFlash?.(ev.x, mouthY, globColor, 5);
              if (particles?.addSporeLob) {
                particles.addSporeLob(ev.x, mouthY, ev.tx, ev.ty, null, globColor, lobOptions);
              } else if (particles?.addBallisticBaseShell) {
                particles.addBallisticBaseShell(ev.x, mouthY, ev.tx, ev.ty, null, globColor);
              } else {
                particles?.addProjectile?.(ev.x, mouthY, { x: ev.tx, y: ev.ty }, globColor, 4, 520);
              }
              particles?.addHitSparks?.(ev.tx, ev.ty, 'bio', 4);
            } else if (uType === 'ProcessingPillar' || uType === 'Processing Pillar') {
              if (audio?.playProcessingPillar) audio.playProcessingPillar();
              else audio?.playLaser?.();
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#a855f7', 8);
            } else if (uType === 'LootingVulture' || uType === 'Looting Vulture' || uType === 'Looting_Vulture') {
              audio?.playLootingApexBeam ? audio.playLootingApexBeam({ x: ev.x }) : audio?.playLaser?.();
              particles?.addTracer?.(ev.x, ev.y, ev.tx, ev.ty, '#fde047', 0.16);
              particles?.addTracer?.(ev.x, ev.y, ev.tx, ev.ty, '#ffffff', 0.08);
              particles?.addLight?.(ev.x, ev.y, 40, '#facc15', 0.3);
              particles?.addHitSparks?.(ev.tx, ev.ty, 'plasma', 5);
            } else if (uType === 'BastionLock' || uType === 'Bastion Lock' || uType === 'Bastion_Lock') {
              if (audio?.playBastionShot) audio.playBastionShot({ x: ev.x });
              else audio?.playGauss?.({ x: ev.x });
              const beamColor = ev.customColor || '#38bdf8';
              const attacker = game.battlefield?.getUnitById?.(ev.id);
              if (attacker) {
                if (attacker._lastBeamTarget) {
                  attacker._lastBeamTarget.x = ev.tx;
                  attacker._lastBeamTarget.y = ev.ty;
                } else {
                  attacker._lastBeamTarget = { x: ev.tx, y: ev.ty };
                }
                attacker._lastBeamTime = attacker.age;
                attacker.barrelHeat = 1.0;
              }
              particles?.addMuzzleFlash?.(ev.x, ev.y, beamColor, 7, { isBastionFlash: true });
              particles?.addBeam?.(ev.x, ev.y, ev.tx, ev.ty, beamColor, 3.2, 0.12, { isBastionBeam: true });
              particles?.addBeam?.(ev.x, ev.y, ev.tx, ev.ty, '#ffffff', 1.2, 0.12, { isBastionBeam: true });
              particles?.addAnamorphicFlare?.(ev.x, ev.y, 50, 2.0, 0.10, '#38bdf8', { isBastionFlare: true });
              particles?.addHitSparks?.(ev.tx, ev.ty, 'plasma', 3);
              particles?.addBastionBurstingParticles?.(ev.tx, ev.ty, 28);
              if (b?.quality !== 'low') {
                particles?.addVaporPuff?.(ev.tx, ev.ty - 4, 8, 0.25);
              }
            } else {
              audio?.playLaser?.();
              particles?.addMuzzleFlash?.(ev.x, ev.y, '#a855f7', 5);
              particles?.addTracer?.(ev.x, ev.y, ev.tx, ev.ty, '#a855f7');
            }
          }

          // Visual reactions: target flashes white, attacker shows barrel
          // heat. Resolved by id (Battlefield.getUnitById(), O(1)) when the
          // event carries one - added alongside x/y/tx/ty specifically so
          // this no longer has to scan every live unit on the field per
          // ATTACK event (docs/ONLINE_PERFORMANCE_AND_CARD_LAG_ANALYSIS.md
          // §3B.1: 25 units × 10 attacks/snapshot × 30 Hz was 7,500
          // proximity checks/sec on the main thread). Falls back to the
          // old proximity scan only if a unit id doesn't resolve (e.g. an
          // older event shape, or the target was a base and has no unit
          // to flash at all, in which case the scan below correctly finds
          // nothing either).
          if (b && b.units) {
            const attacker = ev.attackerUnitId != null ? b.getUnitById?.(ev.attackerUnitId) : null;
            if (attacker) {
              attacker.barrelHeat = 1.0;
            } else {
              for (let j = 0; j < b.units.length; j++) {
                if (Math.hypot(b.units[j].x - ev.x, b.units[j].y - ev.y) < 30) {
                  b.units[j].barrelHeat = 1.0;
                  break;
                }
              }
            }
            const targetUnit = ev.targetUnitId != null ? b.getUnitById?.(ev.targetUnitId) : null;
            if (targetUnit) {
              targetUnit.hitFlashTimer = 0.12;
            } else if (ev.targetUnitId === undefined) {
              // Only scan when the event doesn't even carry the field (a
              // legitimately absent target - e.g. a base - sets
              // targetUnitId to null, not undefined, and should NOT fall
              // back to guessing by proximity).
              for (let j = 0; j < b.units.length; j++) {
                const dx = b.units[j].x - ev.tx;
                const dy = b.units[j].y - ev.ty;
                if (dx * dx + dy * dy < 900) {
                  b.units[j].hitFlashTimer = 0.12;
                  break;
                }
              }
            }
          }
        } else if (ev.type === 'FIRE') {
          particles?.addMuzzleFlash?.(ev.x, ev.y, ev.color || '#ffdd44', 6);
          if (ev.tx !== undefined && ev.ty !== undefined) {
            particles?.addTracer?.(ev.x, ev.y, ev.tx, ev.ty, ev.color || '#ffdd44');
          }
        } else if (ev.type === 'DEFLECTION') {
          particles?.addDeflection?.(ev.x, ev.y, ev.dirX, ev.projectileType, { isHumanShooter: !!ev.isHumanShooter, race: ev.isHumanShooter ? 'Human' : 'Alien' });
          if (ev.isBastionLock) {
            particles?.addBastionShieldRicochet?.(ev.x, ev.y, ev.dirX, 2);
          }
        } else if (ev.type === 'DAMAGE_TEXT') {
          particles?.addDamageText?.(ev.x, ev.y, ev.text, ev.textType);
        } else if (ev.type === 'PROJECTILE_MISS') {
          particles?.addProjectileMiss?.(ev.x, ev.y, ev.projectileType);
        } else if (ev.type === 'AUDIO') {
          if (audio && typeof audio[ev.method] === 'function') {
            audio[ev.method](...(ev.args || []));
          }
        } else if (ev.type === 'SUPERWEAPON') {
          if (ev.superType === 'NUKE') {
            audio?.playNukeAlarm?.();
            particles?.triggerNuclearStrike?.(ev.x, ev.y, () => {
              audio?.playExplosion?.('massive');
              b?.addRadioactiveCrater?.(ev.x, ev.y);
            });
          } else if (ev.superType === 'BIO_PLASMA') {
            audio?.playBioPlasmaHush?.();
            audio?.duckBGM?.(0.15, 1.95);
            particles?.triggerBioPlasmaStrike?.(ev.x, ev.y, () => {
              audio?.playBioPlasmaIncision?.();
              b?.addObsidianGlassField?.(ev.x, ev.y);
            }, () => {});
          }
        } else if (ev.type === 'HIT_SPARKS') {
          particles?.addHitSparks?.(ev.x, ev.y, ev.sparkType, ev.count, ev.dirX);
        } else if (ev.type === 'ACID_SPLASH') {
          particles?.addAcidSplash?.(ev.x, ev.y, ev.count);
        } else if (ev.type === 'SCREEN_SHAKE') {
          particles?.addScreenShake?.(ev.intensity, ev.duration);
        } else if (ev.type === 'SCORCH_MARK') {
          particles?.addScorchMark?.(ev.x, ev.y, ev.radius);
        } else if (ev.type === 'ELECTRIC_SHOCK') {
          particles?.addElectricShock?.(ev.x, ev.y, ev.radius, ev.color);
        } else if (ev.type === 'TESLA_ARC') {
          particles?.addTeslaArc?.(ev.x1, ev.y1, ev.x2, ev.y2, ev.color);
        } else if (ev.type === 'IMPACT') {
          particles?.addExplosion?.(ev.x, ev.y, ev.radius || 18, ev.color || '#facc15');
        } else if (ev.type === 'EXPLOSION') {
          particles?.addExplosion?.(ev.x, ev.y, ev.radius || 20, ev.color || '#ff8800', ev.count || 12);
        } else if (ev.type === 'SHOCKWAVE') {
          particles?.addShockwave?.(ev.x, ev.y, ev.radius || 34, ev.color || '#00f0ff', ev.duration || 0.25);
        } else if (ev.type === 'CAPSULE_LAND') {
          particles?.addShockwave?.(ev.x, ev.y, 34, '#f97316', 0.25);
          particles?.addExplosion?.(ev.x, ev.y, 26, '#facc15', 14);
          audio?.playExplosion?.('small');
        } else if (ev.type === 'EXPLOSIVE_CROW') {
          // Damage/explosion/shockwave/"INSTANT KILL" text already arrived (or will
          // arrive this same tick) as their own separate DAMAGE_TEXT/EXPLOSION/
          // SHOCKWAVE/SCREEN_SHAKE events - the server applied all of that
          // synchronously the instant it queued this event (see addExplosiveCrow in
          // server/gameSimulation.js). This handler only replays the multi-second
          // crow flight/dive-bomb visual itself; its callback stays empty so the
          // impact flourish isn't drawn twice.
          const liveTarget = (ev.targetUnitId != null) ? b?.getUnitById?.(ev.targetUnitId) : null;
          const targetRef = liveTarget || { x: ev.targetX, y: ev.targetY, isDead: true };
          particles?.addExplosiveCrow?.(ev.startX, ev.startY, targetRef, () => {});
        } else if (ev.type === 'REFLECT_SHARD') {
          if (audio?.playDeflected) {
            audio.playDeflected({ x: ev.x });
          } else {
            audio?.playRicochet?.({ x: ev.x });
          }
          const liveTarget = (ev.targetUnitId != null) ? b?.getUnitById?.(ev.targetUnitId) : null;
          const targetRef = liveTarget || { x: ev.tx, y: ev.ty };
          particles?.addChitinShard?.(ev.x, ev.y, targetRef, '#c084fc', 420);
        } else if (ev.type === 'PLASMA_SPEAR') {
          audio?.playLaser ? audio.playLaser() : null;
          const liveTarget = (ev.targetUnitId != null) ? b?.getUnitById?.(ev.targetUnitId) : null;
          const targetRef = liveTarget || { x: ev.tx, y: ev.ty, isAir: ev.targetIsAir };
          particles?.addElasticPlasmaSpear?.(ev.startX, ev.startY, targetRef, 44, ev.team, null, ev.travelTime);
        } else if (ev.type === 'PLASMA_SPHERE') {
          audio?.playExplosion ? audio.playExplosion('small') : null;
          const liveTarget = (ev.targetUnitId != null) ? b?.getUnitById?.(ev.targetUnitId) : null;
          const targetRef = liveTarget || { x: ev.tx, y: ev.ty, isAir: false };
          particles?.addHomingPlasmaSphere?.(ev.startX, ev.startY, targetRef, ev.splashRadius, 40, ev.team, null, ev.flightTimeSec);
        } else if (ev.type === 'SUICIDE_DRONE') {
          audio?.playLaser ? audio.playLaser('small', { x: ev.startX }) : null;
          const liveTarget = (ev.targetUnitId != null) ? b?.getUnitById?.(ev.targetUnitId) : null;
          const targetRef = liveTarget || { x: ev.targetX, y: ev.targetY, isAir: false };
          particles?.addSuicideDrone?.(
            ev.startX,
            ev.startY,
            ev.targetX,
            ev.targetY,
            ev.team,
            999,
            () => {},
            null,
            targetRef,
            ev.speedPx || 260
          );
          if (b?.moduleSystem?.fx?.[ev.team]?.drone_command) {
            const mfx = b.moduleSystem.fx[ev.team].drone_command;
            mfx.launchFlash = 0.35;
            mfx.departTimer = 0.33;
            mfx.departTotal = 0.33;
            mfx.departUntil = performance.now() * 0.001 + 0.33;
          }
        } else if (ev.type === 'MUZZLE_FLASH') {
          particles?.addMuzzleFlash?.(ev.x, ev.y, ev.color, ev.size);
        } else if (ev.type === 'LIGHTNING_ARC') {
          particles?.addLightningArc?.(ev.x1, ev.y1, ev.x2, ev.y2);
        } else if (ev.type === 'BASE_SUL_ORB') {
          if (particles?.addSulEnergyOrb) {
            particles.addSulEnergyOrb(ev.startX, ev.startY, ev.targetX, ev.targetY);
          } else {
            particles?.addBallisticBaseShell?.(ev.startX, ev.startY, ev.targetX, ev.targetY, null, '#d946ef');
          }
          if (b?.bases) {
            const firingBase = (ev.startX < (b.width || 1000) * 0.5) ? b.bases.western : b.bases.eastern;
            if (firingBase) firingBase.recoilTimer = 0.28;
          }
        } else if (ev.type === 'BASE_SHELL') {
          particles?.addBallisticBaseShell?.(ev.startX, ev.startY, ev.targetX, ev.targetY, null, ev.color);
          if (b?.bases) {
            const firingBase = (ev.startX < (b.width || 1000) * 0.5) ? b.bases.western : b.bases.eastern;
            if (firingBase) firingBase.recoilTimer = 0.28;
          }
        } else if (ev.type === 'BEAM_LOCK') {
          audio?.playPreserverBeamPulse?.();
        } else if (ev.type === 'VULTURE_BIO_MISSILE') {
          if (ev.isIntercept) {
            audio?.playVultureIntercept ? audio.playVultureIntercept() : audio?.playVultureBallLaunch?.();
          } else {
            audio?.playVultureBallLaunch?.();
          }
          const liveTarget = (ev.targetUnitId != null) ? b?.getUnitById?.(ev.targetUnitId) : null;
          const targetRef = liveTarget || { x: ev.targetX, y: ev.targetY };
          if (particles?.addVultureBioMissile) {
            particles.addVultureBioMissile(ev.startX, ev.startY, targetRef, ev.color || '#ff0033', ev.speed || 520, null, ev.isIntercept);
          } else if (particles?.addProjectile) {
            particles.addProjectile(ev.startX, ev.startY, targetRef, ev.color || '#ff0033', 5, ev.speed || 520);
          }
          const attacker = ev.attackerUnitId != null ? b?.getUnitById?.(ev.attackerUnitId) : null;
          if (attacker) {
            attacker._vultureRecoil = 5.0;
            if (typeof ev.podIndex === 'number' && attacker.vulturePods?.[ev.podIndex]) {
              attacker.vulturePods[ev.podIndex].reloadTimer = 5.0;
            }
          }
        } else if (ev.type === 'PINK_BERET_ROCKET') {
          const startX = ev.startX ?? ev.x;
          const startY = ev.startY ?? ev.y;
          const trailColor = ev.trailColor || '#ff2a85';
          audio?.playRocketLaunch?.('heavy', { x: startX, unitType: 'PinkBeret' });
          particles?.addMuzzleFlash?.(startX, startY, trailColor, 10);
          particles?.addRisingEmbers?.(startX, startY, 4, 12);
          const liveTarget = (ev.targetUnitId != null) ? b?.getUnitById?.(ev.targetUnitId) : null;
          const targetRef = liveTarget || { x: ev.targetX ?? ev.tx, y: ev.targetY ?? ev.ty };
          if (particles?.addPinkBeretHomingRocket) {
            particles.addPinkBeretHomingRocket(startX, startY, targetRef, ev.speed || 480, null, trailColor);
          } else if (particles?.addProjectile) {
            particles.addProjectile(startX, startY, targetRef, trailColor, 5, ev.speed || 480);
          }
        } else if (ev.type === 'PINK_BERET_DEATH') {
          const deadUnit = (ev.unitId != null) ? b?.getUnitById?.(ev.unitId) : null;
          const unitRef = deadUnit || { x: ev.x, y: ev.y, team: ev.team || 'western', radius: 20, name: 'Pink Beret', unitType: 'PinkBeret', race: 'Human', isPinkBeret: true };
          particles?.addPinkBeretKissDeath?.(unitRef, audio);
        } else if (ev.type === 'DUAL_CURVED_MISSILES') {
          const startX = ev.startX ?? ev.x;
          const startY = ev.startY ?? ev.y;
          const color = ev.color || '#ff8800';
          const liveTarget = (ev.targetUnitId != null) ? b?.getUnitById?.(ev.targetUnitId) : null;
          const targetRef = liveTarget || { x: ev.targetX ?? ev.tx, y: ev.targetY ?? ev.ty };
          particles?.addMuzzleFlash?.(startX, startY, color, 8);
          particles?.addAnamorphicFlare?.(startX, startY, 120, 3.0, 0.15, color);
          particles?.addRisingEmbers?.(startX, startY, 3, 10);
          if (particles?.addDualCurvedMissiles) {
            particles.addDualCurvedMissiles(startX, startY, targetRef, color, ev.size || 5, ev.speed || 480, null, ev.trailColor || null);
          } else {
            particles?.addProjectile?.(startX, startY, targetRef, color, ev.size || 5, ev.speed || 480);
          }
        } else if (ev.type === 'MIDAS_TRANSMUTATION_BEAM') {
          particles?.addMidasTransmutationBeam?.(ev.startX, ev.startY, ev.targetX, ev.targetY);
        } else if (ev.type === 'CLAIMING_PILLAR_BURST') {
          const deadUnit = (ev.unitId != null) ? b?.getUnitById?.(ev.unitId) : null;
          const unitRef = deadUnit || { x: ev.x, y: ev.y, team: ev.team || 'western', radius: 34, name: 'Claiming Pillar', unitType: 'ClaimingPillar', race: 'Sul', isClaimingPillar: true };
          particles?.addClaimingPillarBurst?.(unitRef);
        } else if (ev.type === 'HYPER_PANZER_DEATH') {
          const deadUnit = (ev.unitId != null) ? b?.getUnitById?.(ev.unitId) : null;
          const unitRef = deadUnit || { x: ev.x, y: ev.y, team: ev.team || 'western', radius: 32, name: 'Hyper Panzer', unitType: 'HyperPanzer', race: 'Human', isHyperPanzer: true };
          particles?.addHyperPanzerDeath?.(unitRef, audio);
        } else if (ev.type === 'LOOTING_VULTURE_DEATH') {
          const deadUnit = (ev.unitId != null) ? b?.getUnitById?.(ev.unitId) : null;
          const unitRef = deadUnit || { x: ev.x, y: ev.y, team: ev.team || 'western', radius: 22, name: 'Looting Vulture', unitType: 'LootingVulture', race: 'Sul', isLootingVulture: true };
          particles?.addLootingVultureDeath?.(unitRef, audio);
        } else if (ev.type === 'HACKER_DEATH') {
          const deadUnit = (ev.unitId != null) ? b?.getUnitById?.(ev.unitId) : null;
          const unitRef = deadUnit || { x: ev.x, y: ev.y, team: ev.team || 'western', radius: 18, name: 'Hacker', unitType: 'Hacker', race: 'Human', isHacker: true };
          particles?.addHackerDeath?.(unitRef, audio);
        } else if (ev.type === 'PULP_DEATH') {
          const deadUnit = (ev.unitId != null) ? b?.getUnitById?.(ev.unitId) : null;
          const unitRef = deadUnit || { x: ev.x, y: ev.y, team: ev.team || 'western', radius: 18, name: 'Pulp', unitType: 'Pulp', race: 'Sul', isPulp: true };
          particles?.addPulpPencilTrickDeath?.(unitRef, audio);
        } else if (ev.type === 'PARASITE_LEAP') {
          const liveTarget = (ev.targetUnitId != null) ? b?.getUnitById?.(ev.targetUnitId) : null;
          const targetRef = liveTarget || { x: ev.targetX, y: ev.targetY };
          particles?.addParasiteLeap?.(ev.startX, ev.startY, targetRef, () => {});
        } else if (ev.type === 'PARASITE_VULTURE_DEATH') {
          const deadUnit = (ev.unitId != null) ? b?.getUnitById?.(ev.unitId) : null;
          const unitRef = deadUnit || { x: ev.x, y: ev.y, team: ev.team || 'western', radius: 22, name: 'Parasite Vulture', unitType: 'ParasiteVulture', race: 'Sul', isParasiteVulture: true };
          particles?.addParasiteVultureDeath?.(unitRef, audio);
        } else if (ev.type === 'PIGEON_BOMB_DROP') {
          const startX = ev.startX ?? ev.x;
          const startY = ev.startY ?? ev.y;
          const destX = ev.targetX ?? ev.tx ?? startX;
          const destY = ev.targetY ?? ev.ty ?? startY;
          if (audio?.playA9PigeonBombDrop) {
            audio.playA9PigeonBombDrop({ x: startX });
          } else {
            audio?.playRocketLaunch?.('heavy', { x: startX, unitType: 'A9Pigeon' });
          }
          particles?.addA9PigeonBombDrop?.(startX, startY, destX, destY, ev.team || 'western', ev.duration || 0.55, audio, true);
        } else if (ev.type === 'A9_PIGEON_DEATH') {
          const deadUnit = (ev.unitId != null) ? b?.getUnitById?.(ev.unitId) : null;
          const unitRef = deadUnit || { x: ev.x, y: ev.y, team: ev.team || 'western', radius: 20, name: 'A9 Pigeon', unitType: 'A9Pigeon', race: ev.race || 'Human', isAir: true, isA9Pigeon: true };
          particles?.addA9PigeonDeathFlock?.(unitRef, audio);
        }
      }
    }

    // 7. Live Combat Performance Grade feed (js/ui/hud.js updateLiveGamerank()).
    // Offline play accumulates b.stats locally every frame; online, only the
    // server's Battlefield ticks MatchStats.update(), so its numbers ride
    // along on this snapshot instead - myStats/duration/totalTerritoryIntegral
    // are computed per-recipient server-side (server/gameSimulation.js
    // serializeStateForTeam()), so this never sees the opponent's own stats.
    if (Number.isFinite(packet.duration) && b?.stats) {
      b.stats.duration = packet.duration;
    }
    if (packet.myStats && game.myTeam && b?.stats?.teams?.[game.myTeam]) {
      Object.assign(b.stats.teams[game.myTeam], packet.myStats);
    }
    // Nuke/Bio-Plasma button: online NUKE commands are applied only on the
    // server (js/main.js Game.enqueueCommand() sends CLIENT_CMD instead of
    // running the local CommandProcessor), so the issuing client's own
    // superweaponCharges counter never gets zeroed by itself - it has to
    // ride along on the snapshot like myStats above, or the button never
    // disables after use even though the server correctly blocks a second nuke.
    if (Number.isFinite(packet.myNukeCharge) && game.myTeam && game.superweaponCharges) {
      game.superweaponCharges[game.myTeam] = packet.myNukeCharge;
      game.updateSuperButtonState?.();
    }
    if (Number.isFinite(packet.totalTerritoryIntegral)) {
      game._onlineTotalTerritoryIntegral = packet.totalTerritoryIntegral;
    }

    return true;
  }

  // Feeds the in-game net overlay's online panel for the WebSocket
  // architecture (js/engine/performance.js._updateNetOverlay(), K-3.3) -
  // the equivalent of the old TurnScheduler.getNetStats() this replaces,
  // but shaped around what actually exists in server-authoritative state
  // streaming (a snapshot rate + its jitter) rather than a lockstep
  // buffer/CRC-desync count, which have no meaning here: the client never
  // runs its own simulation to diverge from the server in the first place.
  // Returns null before the first two snapshots have arrived (nothing to
  // measure a gap between yet).
  getNetStats() {
    if (this._snapshotIntervalsMs.length === 0) return null;
    const intervals = this._snapshotIntervalsMs;
    const avgMs = intervals.reduce((sum, v) => sum + v, 0) / intervals.length;
    const sumDev = intervals.reduce((sum, v) => sum + Math.abs(v - avgMs), 0);
    const jitterMs = sumDev / intervals.length;
    return {
      pingMs: this.game?.wsClient?.lastPingMs ?? null,
      snapshotHz: avgMs > 0 ? 1000 / avgMs : 0,
      jitterMs,
      peakJitterMs: Math.max(...intervals) - Math.min(...intervals)
    };
  }

  // Continuous 60 FPS Entity Interpolation & Visual Loop
  updateInterpolation(game, dt) {
    const b = game?.battlefield;
    if (!b || !b.units) return;

    const movingBlend = 1.0 - Math.exp(-14 * dt);
    const stationaryBlend = 1.0 - Math.exp(-22 * dt);

    for (let i = 0; i < b.units.length; i++) {
      const u = b.units[i];
      if (u.isDead) continue;

      // 1. Continuous smooth 60 FPS dead-reckoning & position interpolation
      if (u.targetX !== undefined) {
        const dx = u.targetX - u.x;
        const dy = u.targetY - u.y;
        if (dx * dx + dy * dy > 3600) {
          u.x = u.targetX;
          u.y = u.targetY;
        } else {
          // If unit is moving (not stationary in combat/structure/held):
          // Advance with its physical speed and gently correct residual error.
          // This eliminates sawtooth deceleration/acceleration between snapshot packets!
          const isMoving = (!u.isAttacking || (u.isHyperPanzer && !u.isStoppedAtBase) || (u.isA9Pigeon && !u.isHovering)) && !u.isHeld && !u.isRooted && !u.isDisabled && !u.isStructure && !u.isGoldenStatue && !u.isCocoon && !u.isBurrowed && !u.isBurrowing && !u.isMineRising && (u.speed > 0) && (!u.deployProgress || u.deployProgress <= 0);
          if (isMoving) {
            const dir = (u.facingDir !== undefined) ? u.facingDir : (u.team === 'western' ? 1 : -1);
            const moveSign = u.isFlyingBackward ? -1 : 1;
            u.x += dir * moveSign * u.speed * dt;
            u.x += (u.targetX - u.x) * movingBlend;
            u.y += (u.targetY - u.y) * movingBlend;
          } else {
            // Stationary / in combat / structure: converge directly
            u.x += dx * stationaryBlend;
            u.y += dy * stationaryBlend;
          }
        }
      }

      // 2. Visual Timers (solid opacity & smooth decay)
      u.age = (u.age || 0) + dt;
      if (u.materializeTimer > 0) u.materializeTimer = 0; // 100% solid opacity
      if (u.hitFlashTimer > 0) u.hitFlashTimer = Math.max(0, u.hitFlashTimer - dt);
      if (u.barrelHeat > 0) u.barrelHeat = Math.max(0, u.barrelHeat - dt * 0.65);
      if (u.dustTimer > 0) u.dustTimer = Math.max(0, u.dustTimer - dt);
      if (u.burrowTimer > 0) u.burrowTimer = Math.max(0, u.burrowTimer - dt);
      if (u.mineRiseTimer > 0) u.mineRiseTimer = Math.max(0, u.mineRiseTimer - dt);
      if (u.isGrowing) {
        const dur = u.growthDuration || 3.0;
        u.growthTimer = Math.min(dur, (u.growthTimer || 0) + dt);
        if (u.growthTimer >= dur) u.isGrowing = false;
      }
      if (u._hpRegenFlash > 0) u._hpRegenFlash = Math.max(0, u._hpRegenFlash - dt);
      if (u.isTranscendent && u.transcendentTimer > 0) {
        u.transcendentTimer = Math.max(0, u.transcendentTimer - dt);
        u.hp = Math.max(1, Math.round((u.transcendentTimer / 10.0) * (u.maxHp || 35)));
      }
      if (u.isHacking && u.hackerTargetId) {
        u.hackerLockTimer = (u.hackerLockTimer || 0) + dt;
      }
      if (u.handRevealTimer > 0) {
        u.handRevealTimer = Math.max(0, u.handRevealTimer - dt);
      }
      if (u.attackCooldown > 0) u.attackCooldown = Math.max(0, u.attackCooldown - dt);
      if (u._vultureRecoil > 0) u._vultureRecoil = Math.max(0, u._vultureRecoil - dt * 15);
      if (u.vulturePods && u.vulturePods.length > 0) {
        for (let p = 0; p < u.vulturePods.length; p++) {
          if (u.vulturePods[p].reloadTimer > 0) {
            u.vulturePods[p].reloadTimer = Math.max(0, u.vulturePods[p].reloadTimer - dt);
          }
        }
      }

      // 3. Resolve Target for Aiming & Combat Animation
      if (u.targetId) {
        if (u.targetId === 'base_western') {
          u.target = b.bases.western;
        } else if (u.targetId === 'base_eastern') {
          u.target = b.bases.eastern;
        } else {
          u.target = b.unitMap?.get(u.targetId) || null;
        }
        if (u.target && u.target.isDead) u.target = null;
      } else {
        u.target = null;
      }

      // 4. Facing direction towards target or movement
      if (u.isA9Pigeon) {
        u.facingDir = u.team === 'western' ? 1 : -1;
      } else if (u.target) {
        u.facingDir = Math.sign(u.target.x - u.x) || (u.team === 'western' ? 1 : -1);
      }
    }

    // 5. Precision Strikes Smooth Advance & Bomb Drop Interpolation
    if (b.activePrecisionStrikes && b.activePrecisionStrikes.length > 0) {
      for (let i = 0; i < b.activePrecisionStrikes.length; i++) {
        const strike = b.activePrecisionStrikes[i];
        strike.shadowX += strike.shadowDir * strike.shadowSpeed * dt;
        if (strike.state === 'BOMB_FALLING' && strike.bombAltitude > 0) {
          const fallSpeed = 380 / (strike.bombDuration || 0.65);
          strike.bombAltitude = Math.max(0, strike.bombAltitude - fallSpeed * dt);
        }
      }
    }

    // 6. Base Turrets recoil decay & target tracking for online rendering
    if (b.bases) {
      const bases = [b.bases.western, b.bases.eastern];
      const enemyTeams = ['eastern', 'western'];
      for (let bi = 0; bi < 2; bi++) {
        const base = bases[bi];
        if (!base) continue;
        if (base.recoilTimer > 0) {
          base.recoilTimer = Math.max(0, base.recoilTimer - dt);
        }
        base._aimRetargetTimer = (base._aimRetargetTimer || 0) - dt;
        const currentTargetValid = base.targetEnemy && !base.targetEnemy.isDead && !base.targetEnemy.isUntargetable && !base.targetEnemy.isGoldenStatue;
        if (currentTargetValid && base._aimRetargetTimer > 0) {
          continue;
        }
        base._aimRetargetTimer = 0.1;
        const enemyTeam = enemyTeams[bi];
        let closestActive = null, closestActiveDstSq = Infinity;
        let closestAny = null, closestAnyDstSq = Infinity;
        const baseRangeSq = (base.range || 312) * (base.range || 312);
        for (let ui = 0; ui < b.units.length; ui++) {
          const eu = b.units[ui];
          if (eu.team !== enemyTeam || eu.isDead || eu.isParachuting || eu.isCinematic || eu.isUntargetable || eu.isGoldenStatue) continue;
          if (eu.isCloaked && !b.isPositionDetected?.(base.team, eu.x, eu.y)) continue;
          const dx = eu.x - base.x;
          const dy = eu.y - base.y;
          const dstSq = dx * dx + dy * dy;
          if (dstSq > baseRangeSq) continue;
          if (dstSq < closestAnyDstSq) { closestAny = eu; closestAnyDstSq = dstSq; }
          if (eu.target === base && dstSq < closestActiveDstSq && b.isInRange?.(eu, base)) {
            closestActive = eu; closestActiveDstSq = dstSq;
          }
        }
        base.targetEnemy = closestActive || closestAny;
      }
    }
  }
}
